import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TabToggle } from "@app/components/tab-toggle";
import { CommentCard, COMMENT_CARD_VARIANT } from "@app/components/base/comment-card/comment-card";
import { TimelineItem } from "@app/components/base/timeline-item/timeline-item";
import { Banner, BANNER_TONE } from "@app/components/base/banner/banner";
import { Spinner as SpinnerIcon } from "@app/components/base/spinner/spinner";
import { LoadingState, LOADING_STATE_VARIANT } from "@app/components/base/loading-state/loading-state";
import { compareDatesDesc, formatDate as formatGlobalDate, formatDateWithToken, isIsoDateKey } from "@app/utils/date";
import { useAccountingApproval } from "@app/hooks/use-accounting-approval";
import { useCan } from "@app/hooks/use-can";
import { ACTIONS } from "@app/auth";
import { ITEMS_COLUMN_SEARCH_HISTORY_KEY, REPORTS_COLUMN_SEARCH_HISTORY_KEY } from "@app/constants/storage-keys";
import type { ReportSortBy, ApproverReportsColumnFilter, ReportDateRange } from "@app/hooks/use-accounting-approval";
import type {
    ExpenseItem,
    ExpenseReport,
    FullExpenseDetail,
    ItemStatus,
    ItemsSummary,
    LostReceiptDeclaration,
    PolicyViolation,
    ReportExpenseItem,
    ReportExpensesData,
    ReportsSummary,
    ReportStatus,
} from "@app/types/accounting";
import { ItemStatusValue, ExpenseAction, ReportStatusValue } from "@app/types/accounting";
import {
    TableLoadingOverlay,
    ButtonUtility,
    Button,
    Input,
    TextArea,
    Select,
    DatePicker,
    ModalOverlay,
    Modal,
    Dialog,
    TooltipTrigger,
} from "@its-engineering-and-development/ui-foundation/components";
import { BlockedActionTooltip } from "@app/components/base/blocked-action-tooltip";
import { CannotApproveReportModal, PolicyViolationModal } from "@app/components/accounting-approval/policy-violation-modals";
import type { PolicyViolationModalProps } from "@app/components/accounting-approval/policy-violation-modals";
import {
    getApprovalBlockingKinds,
    getNonReceiptViolations,
    getPopupViolations,
    getReceiptIndicator,
    getViolationPopupTone,
    hasBlockingViolation,
    ITEM_VIOLATION_APPROVE_TOOLTIP,
    VIOLATION_ACTION_REQUIRE_EXPLANATION,
    violationNeedsJustification,
    violationHasExplanation,
    violationRequiresExplanation,
} from "@app/utils/policy-violations";
import type { ApprovalBlockingKinds, ViolationEvaluableExpense } from "@app/utils/policy-violations";
import { ColumnFilterPopover, type SortDirection } from "@/components/base/column-filter-popover";
import { ColumnHeader } from "@/components/base/column-header";
import type { TablePageInfo } from "@its-engineering-and-development/ui-foundation/components";
import { DataTablePagination, DataTableLoading, tableLoadingLabel } from "@/components/base/data-table/data-table";
import type { CalendarDate } from "@internationalized/date";
import { parseDate } from "@internationalized/date";
import { PAYMENT_METHOD } from "@app/constants/payment-methods";
import { TAB, SUB_TAB, DETAIL_TAB } from "@app/constants/tabs";
import type { ActiveSubTab, DetailTab } from "@app/constants/tabs";
import { FilePreviewCard, FileViewer, formatDate, MimeType } from "@app/utils/ui-foundation-compat";
import type { FileViewerFile } from "@app/utils/ui-foundation-compat";
import { ExpenseCategories, DynamoDBFieldNames, LAYOUT, expenseInputTypes, isMileage } from "@app/constants/expense-categories";
import type { ExpenseCategoryField } from "@app/constants/expense-categories";
import type { Category } from "@app/types/categories";
import { CATEGORY_STATUS, CATEGORIES_LOAD_STATUS } from "@app/types/categories";
import { DropdownSelector } from "@app/components/base/dropdown-selector/dropdown-selector";
import { useLocation, useNavigate } from "react-router";
import { useNotifications } from "@its-engineering-and-development/ui-foundation/providers";
import { getApprovalSubTabFromSearch, isValidApprovalTab, QUERY_PARAM, ROUTE_PATH, SUB_TAB_TO_ROUTE } from "@/constants/navigation-routes";

type PolicyViolationModalState = Omit<PolicyViolationModalProps, "onDismiss">;

// ─── Expense detail panel — category-field helpers ──────────────────────────

/** stateName values that are always rendered in fixed positions in the detail panel */
const FIXED_DETAIL_FIELD_STATES = new Set<string>([
    DynamoDBFieldNames.MERCHANT,
    DynamoDBFieldNames.PAYMENT,
    DynamoDBFieldNames.AMOUNT,
    DynamoDBFieldNames.CURRENCY,
    DynamoDBFieldNames.CHARGE_DATE,
    DynamoDBFieldNames.PURPOSE,
    DynamoDBFieldNames.ACCOUNT_CODE,
    DynamoDBFieldNames.CARD_CURRENCY_AMOUNT,
    DynamoDBFieldNames.SETTLE_DATE,
]);

const DATE_PICKER_FIELD_CLASS =
    "w-full [&_button]:!h-11 [&_button]:!w-full [&_button]:!justify-between [&_button]:!rounded-lg [&_button]:!border [&_button]:!border-gray-300 [&_button]:!px-3 [&_button]:!text-md [&_button]:!font-normal [&_button]:!shadow-none";

/**
 * Broadcasts whether the current user may perform approval write actions
 * (approve, deny, send back, edit category, comment) to deeply nested table and
 * detail components without prop-drilling. Defaults to true so isolated tests and
 * any usage outside the provider keep their existing enabled behavior.
 */
const ApprovalWriteAccessContext = createContext(true);

/** Single access point for the approval write-access flag broadcast by ApprovalView. */
function useApprovalWriteAccess(): boolean {
    return useContext(ApprovalWriteAccessContext);
}

function ReportCommentsInline({
    reportId,
    getReportComments,
    textRef,
    disabled,
}: {
    reportId: string;
    getReportComments?: (reportId: string) => Promise<any>;
    textRef?: React.MutableRefObject<string>;
    disabled?: boolean;
}) {
    const [text, setText] = useState("");
    const [comments, setComments] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);

    // Keep a stable ref so the fetch effect is not re-triggered by function identity changes
    // (e.g. when sibling queries like ReportExpenses complete and recreate callback references).
    const getReportCommentsRef = useRef(getReportComments);
    useEffect(() => {
        getReportCommentsRef.current = getReportComments;
    });

    // Guard against React Strict Mode's double-invoke of effects in development,
    // while still allowing a new request when reportId changes.
    const lastFetchedReportIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (lastFetchedReportIdRef.current === reportId) return;
        const fetchFn = getReportCommentsRef.current;
        if (!fetchFn) return;

        lastFetchedReportIdRef.current = reportId;
        setLoading(true);
        setFetchError(null);
        void fetchFn(reportId)
            .then((res) => {
                const items = Array.isArray(res) ? res : (res?.items ?? []);
                setComments(items as any[]);
            })
            .catch((err: unknown) => {
                const msg = err instanceof Error ? err.message : "Failed to load comments";
                setFetchError(msg);
            })
            .finally(() => setLoading(false));
    }, [reportId]);

    return (
        <div>
            {loading ? (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                    <SpinnerIcon className="h-3.5 w-3.5 text-gray-400" />
                    <span>Loading comments…</span>
                </div>
            ) : fetchError ? (
                <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{fetchError}</div>
            ) : comments.length === 0 ? (
                <p className="text-sm text-gray-400">No comments</p>
            ) : (
                <div className="space-y-3">
                    {comments.map((c: any, idx: number) => (
                        <CommentCard
                            key={c.commentId ?? idx}
                            variant={COMMENT_CARD_VARIANT.Plain}
                            text={c.text}
                            author={c.authorFullName ?? c.authorId}
                            date={c.createdAt}
                        />
                    ))}
                </div>
            )}

            {!disabled && (
                <div className="mt-3">
                    <textarea
                        className="w-full rounded border border-gray-200 p-2 text-sm"
                        rows={3}
                        value={text}
                        onChange={(e) => {
                            setText(e.target.value);
                            if (textRef) textRef.current = e.target.value;
                        }}
                        placeholder="Add a comment (optional)"
                    />
                </div>
            )}
        </div>
    );
}

// Cache maps derived from the `allCategories` array to avoid repeated O(n) lookups
type CategoryLookup = {
    fullMap: Map<string, Category>;
    activeList: Category[];
};
const categoryArrayCache = new WeakMap<Category[], CategoryLookup>();
function getCategoryLookup(allCategories: Category[] | null | undefined): CategoryLookup {
    if (!allCategories) return { fullMap: new Map(), activeList: [] };
    let cached = categoryArrayCache.get(allCategories);
    if (!cached) {
        const activeList = allCategories.filter((c) => String(c.status).toLowerCase() === CATEGORY_STATUS.Active);
        cached = {
            fullMap: new Map(allCategories.map((c) => [c.categoryId, c])),
            activeList,
        };
        categoryArrayCache.set(allCategories, cached);
    }
    return cached;
}

function getCategoryMap(allCategories: Category[] | null | undefined): Map<string, Category> {
    return getCategoryLookup(allCategories).fullMap;
}

const DELETED_CATEGORY_LABEL = "Deleted category";
const NO_CATEGORY_LABEL = "—";
const CATEGORY_ITEM_APPROVE_TOOLTIP = "Category must be specified before this expense can be approved";

// A deleted category is handled exactly like a deactivated one; only the wording differs.
const BLOCKED_CATEGORY_COPY = {
    [CATEGORY_STATUS.Deactivated]: {
        prefix: "(Deactivated) ",
        title: "Deactivated category",
        message: "One or more expenses have a deactivated category selected. Please re-select a valid category before this report can move forward.",
    },
    [CATEGORY_STATUS.Deleted]: {
        prefix: "(Deleted) ",
        title: "Deleted category",
        message: "One or more expenses have a deleted category selected. Please re-select a valid category before this report can move forward.",
    },
} as const;

type BlockedCategoryStatus = keyof typeof BLOCKED_CATEGORY_COPY;

/** Finds a category of any status by ULID categoryId, falling back to a numeric legacyId string (e.g. "23"). */
function findCategory(categoryId: string, allCategories: Category[]): Category | undefined {
    const lookup = getCategoryLookup(allCategories);
    const match = lookup.fullMap.get(categoryId);
    if (match) return match;
    const legacyId = Number(categoryId);
    if (!Number.isFinite(legacyId)) return undefined;
    return lookup.activeList.find((c) => c.legacyId === legacyId) ?? allCategories.find((c) => c.legacyId === legacyId);
}

/** The blocked status (deactivated or deleted) of a category, or undefined when it is selectable or unknown. */
function getBlockedCategoryStatus(category: Category | undefined): BlockedCategoryStatus | undefined {
    const status = String(category?.status).toLowerCase();
    return status in BLOCKED_CATEGORY_COPY ? (status as BlockedCategoryStatus) : undefined;
}

function getCategoryBlockStatus(categoryId: string | null | undefined, allCategories: Category[]): BlockedCategoryStatus | undefined {
    if (!categoryId) return undefined;
    return getBlockedCategoryStatus(findCategory(categoryId, allCategories));
}

/** Same lookup for an expense item: `category` first (ULID), then its legacy `subCategoryId` (matched against Category.legacyId). */
function findItemCategory(item: { category: string; subCategoryId?: number | string | null }, allCategories: Category[]): Category | undefined {
    const byCategory = item.category ? findCategory(item.category, allCategories) : undefined;
    if (byCategory || item.subCategoryId == null) return byCategory;
    const legacyId = Number(item.subCategoryId);
    return Number.isFinite(legacyId) ? allCategories.find((c) => c.legacyId === legacyId) : undefined;
}

function getItemCategoryBlockStatus(
    item: { category: string; subCategoryId?: number | string | null },
    allCategories: Category[],
): BlockedCategoryStatus | undefined {
    return getBlockedCategoryStatus(findItemCategory(item, allCategories));
}

/**
 * Resolves a category display name from a ULID categoryId or legacy name string.
 * Approved reports show the frozen name when available (no prefix or indicator), otherwise the real
 * name from the complete list. Other statuses show "(Deactivated) {name}" / "(Deleted) {name}" for deactivated / deleted categories.
 * Returns "—" when no category is assigned, or "Deleted category" when an id fails to resolve.
 */
function resolveCategoryName(categoryId: string, allCategories: Category[], reportStatus?: ReportStatus, frozenName?: string | null): string {
    if (!categoryId) return NO_CATEGORY_LABEL;
    const isApproved = reportStatus === ReportStatusValue.Approved;
    if (isApproved && frozenName) return frozenName;
    const match = findCategory(categoryId, allCategories);
    if (!match) return DELETED_CATEGORY_LABEL;
    const status = String(match.status).toLowerCase();
    if (isApproved || status === CATEGORY_STATUS.Active) return match.categoryName;
    const blockedStatus = getBlockedCategoryStatus(match);
    return blockedStatus ? `${BLOCKED_CATEGORY_COPY[blockedStatus].prefix}${match.categoryName}` : DELETED_CATEGORY_LABEL;
}

function isMileageCategoryId(categoryId: string, allCategories: Category[]): boolean {
    if (!categoryId) return false;
    // 1. Try by ULID categoryId
    const map = getCategoryMap(allCategories);
    const match = map.get(categoryId);
    if (match) return isMileage(match.legacyId);
    // 2. Fallback: match by numeric legacyId string
    const numericId = Number(categoryId);
    if (Number.isFinite(numericId) && !Number.isNaN(numericId)) {
        const legacyMatch = allCategories.find((c) => c.legacyId === numericId);
        return isMileage(legacyMatch?.legacyId ?? numericId);
    }
    return false;
}

function resolveReportExpenseMerchant(merchant: string, categoryId: string, allCategories: Category[]): string {
    return isMileageCategoryId(categoryId, allCategories) ? "-" : merchant;
}

function isMileageItem(item: ExpenseItem, allCategories: Category[]): boolean {
    if (isMileage(item.subCategoryId)) return true;
    return isMileageCategoryId(item.category, allCategories);
}

function resolveItemMerchant(item: ExpenseItem, allCategories: Category[]): string {
    return isMileageItem(item, allCategories) ? "-" : item.merchant;
}

/** True when an expense in a non-approved report cannot be approved at item level because its category is deactivated or deleted. */
function isItemApproveBlocked(
    item: { category: string; subCategoryId?: number | string | null },
    allCategories: Category[],
    reportStatus: ReportStatus,
): boolean {
    return reportStatus !== ReportStatusValue.Approved && !!getItemCategoryBlockStatus(item, allCategories);
}

/** Category cell for an expense in an expanded report; deactivated/deleted categories on non-approved reports show "(Deactivated|Deleted) {name}" in red. */
function ReportExpenseCategory({ item, allCategories, reportStatus }: { item: ReportExpenseItem; allCategories: Category[]; reportStatus: ReportStatus }) {
    const blockedStatus = isItemApproveBlocked(item, allCategories, reportStatus) ? getItemCategoryBlockStatus(item, allCategories) : undefined;
    if (!blockedStatus) {
        return <span>{resolveCategoryName(item.category, allCategories, reportStatus, item.frozenCategory?.categoryName)}</span>;
    }
    return (
        <span className="text-red-600">
            {BLOCKED_CATEGORY_COPY[blockedStatus].prefix}
            {findItemCategory(item, allCategories)?.categoryName}
        </span>
    );
}

/**
 * Returns the category-specific extra fields for a given subcategory id.
 * Fixed fields (Merchant, Payment, Amount, Currency, Charge Date, Description, Account Code,
 * Card Currency Amount, Settle Date) are excluded because they are always rendered
 * in hardcoded positions in the detail panel.
 */
function getCategoryExtraFields(subCategoryId: number | null): ExpenseCategoryField[] {
    if (subCategoryId == null) return [];
    for (const cat of ExpenseCategories) {
        for (const sub of cat.subCategories) {
            if (sub.subCategoryId === subCategoryId) {
                return sub.formFields.filter((field) => !FIXED_DETAIL_FIELD_STATES.has(field.stateName));
            }
        }
    }
    return [];
}

/**
 * Groups a flat list of fields into row arrays following the same pairing logic
 * as buildRows in ExpenseDynamicForm: SINGLE_COLUMN fields are paired left-right;
 * FULL_WIDTH fields always occupy their own row.
 */
function buildExtraFieldRows(fields: ExpenseCategoryField[]): ExpenseCategoryField[][] {
    const rows: ExpenseCategoryField[][] = [];
    let pending: ExpenseCategoryField | null = null;

    for (const field of fields) {
        const span = field.layoutConfig.span;
        if (span === LAYOUT.FULL_WIDTH) {
            if (pending) {
                rows.push([pending]);
                pending = null;
            }
            rows.push([field]);
        } else {
            if (pending) {
                rows.push([pending, field]);
                pending = null;
            } else {
                pending = field;
            }
        }
    }
    if (pending) rows.push([pending]);
    return rows;
}

// Date-typed field state names — rendered as date-only for now.
// TODO: time support not yet implemented; format is intentionally date-only until
//       the API consistently returns datetime values.
const DATE_EXTRA_FIELD_STATES = new Set<string>([DynamoDBFieldNames.DEPARTURE_DATE, DynamoDBFieldNames.CHECK_IN_DATE, DynamoDBFieldNames.PICK_UP_DATE]);

/**
 * Maps an ExpenseCategoryField to its display value from a FullExpenseDetail.
 * Uses `field.apiKey` for direct property lookup; returns "—" when the field
 * has no API mapping yet or the value is null/undefined.
 */
function getExpenseDetailFieldValue(item: FullExpenseDetail, field: ExpenseCategoryField): string {
    if (!field.apiKey) return "—";

    const val = (item as unknown as Record<string, unknown>)[field.apiKey];
    if (val == null) return "—";

    if (Array.isArray(val)) {
        return (val as string[]).length > 0 ? (val as string[]).join(", ") : "—";
    }

    if (DATE_EXTRA_FIELD_STATES.has(field.stateName)) {
        // TODO: time not yet supported — date-only format
        return formatDate(String(val), "MMMM d, yyyy");
    }

    return String(val);
}

const STATUS_FILTER_OPTIONS: Record<ActiveSubTab, Array<{ label: string; value: ReportStatus }>> = {
    [SUB_TAB.Preview]: [
        { label: "Approved", value: "APPROVED" },
        { label: "Pending", value: "PENDING" },
        { label: "In Review", value: "IN_REVIEW" },
        { label: "Submitted", value: "SUBMITTED" },
    ],
    [SUB_TAB.Review]: [
        { label: "Pending", value: "PENDING" },
        { label: "In Review", value: "IN_REVIEW" },
    ],
    [SUB_TAB.Completed]: [{ label: "Approved", value: "APPROVED" }],
};

import { formatCurrency } from "@app/utils/currency";

// ─── Helpers ────────────────────────────────────────────────────────────────

function fmt$(v: number) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);
}

function fmtDate(s: string) {
    return formatGlobalDate(s);
}

function toInputDateValue(date: Date) {
    return date.toISOString().slice(0, 10);
}

function normalizeDateString(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const dateOnly = trimmed.includes("T") ? trimmed.slice(0, 10) : trimmed;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return dateOnly;

    const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateOnly);
    if (!slashMatch) return null;

    const month = Number(slashMatch[1]);
    const day = Number(slashMatch[2]);
    const year = slashMatch[3];
    if (!Number.isFinite(month) || !Number.isFinite(day)) return null;

    const paddedMonth = String(month).padStart(2, "0");
    const paddedDay = String(day).padStart(2, "0");
    return `${year}-${paddedMonth}-${paddedDay}`;
}

function toDateValue(value: unknown): CalendarDate | null {
    if (typeof value !== "string") return null;
    const normalized = normalizeDateString(value);
    if (!normalized) return null;

    try {
        return parseDate(normalized);
    } catch {
        return null;
    }
}

/**
 * Determines if a report can be approved based on expense statuses.
 *
 * Rules:
 * - If ANY expense is REJECTED, report cannot be approved
 * - If NO expenses are REJECTED:
 *   - All PENDING expenses can be approved (whole report gets approved)
 *   - Mix of PENDING and APPROVED expenses can be approved
 *
 * @param reportItems - Array of expense items in the report
 * @returns true if report can be approved, false otherwise
 */
function canApproveReport(reportItems: Array<{ status: ItemStatus }>): boolean {
    if (reportItems.length === 0) return false;

    // If any expense is rejected, cannot approve the report
    const hasRejectedItems = reportItems.some((item) => item.status === ItemStatusValue.Rejected);
    if (hasRejectedItems) return false;

    // If no rejected items and all items are either PENDING or APPROVED, can approve
    return reportItems.every((item) => item.status === ItemStatusValue.Pending || item.status === ItemStatusValue.Approved);
}

/**
 * Gets a descriptive title for why a report cannot be approved.
 */
function getApprovalDisabledReason(reportItems: Array<{ status: ItemStatus }>): string {
    if (reportItems.length === 0) return "No expense items in report";

    const rejectedCount = reportItems.filter((item) => item.status === ItemStatusValue.Rejected).length;
    if (rejectedCount > 0) return `Cannot approve: ${rejectedCount} expense(s) have been denied`;

    return "Cannot approve this report";
}

function StatusBadge({ status }: { status: string }) {
    const map: Record<string, string> = {
        SUBMITTED: "bg-amber-100 text-amber-700",
        IN_REVIEW: "bg-amber-100 text-amber-700",
        APPROVED: "bg-green-100 text-green-700",
        UPLOADED: "bg-blue-100 text-blue-700",
        PENDING: "bg-amber-100 text-amber-700",
        REJECTED: "bg-red-100 text-red-700",
    };
    const label: Record<string, string> = {
        SUBMITTED: "Submitted",
        IN_REVIEW: "In Review",
        APPROVED: "Approved",
        UPLOADED: "Uploaded",
        PENDING: "Pending",
        REJECTED: "Rejected",
    };
    return (
        <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${map[status] ?? "bg-gray-100 text-gray-700"}`}>
            {label[status] ?? status}
        </span>
    );
}

// ─── Icons (inline SVG) ──────────────────────────────────────────────────────

function IconCheckCircle({ missing = false }: { missing?: boolean }) {
    if (missing) {
        return (
            <svg className="ml-3.5 h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <circle cx="12" cy="12" r="9" />
                <path strokeLinecap="round" d="M9 9l6 6m0-6l-6 6" />
            </svg>
        );
    }
    return (
        <svg className="ml-3.5 h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <circle cx="12" cy="12" r="9" />
            <path strokeLinecap="round" d="M8 12l3 3 5-5" />
        </svg>
    );
}

// Red exclamation shown in the Receipt column when a receipt is required but missing.
function IconReceiptWarning({ className = "ml-3.5 h-4 w-4 text-red-500" }: { className?: string }) {
    return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} role="img" aria-label="Receipt policy violation">
            <circle cx="12" cy="12" r="9" />
            <path strokeLinecap="round" d="M12 7.5v5" />
            <path strokeLinecap="round" d="M12 16h.01" />
        </svg>
    );
}

// ─── AI Transparency Indicators ──────────────────────────────────────────────

const AI_GENERATED_BY_VALUE = "ExpenseAI";

function isAiGenerated(item: { generatedBy?: string | null }): boolean {
    return item.generatedBy === AI_GENERATED_BY_VALUE;
}

function IconAiSparkle({ className = "h-4 w-4 text-blue-500" }: { className?: string }) {
    return (
        <svg className={className} width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
                d="M4.5 22V17M4.5 7V2M2 4.5H7M2 19.5H7M13 3L11.2658 7.50886C10.9838 8.24209 10.8428 8.60871 10.6235 8.91709C10.4292 9.1904 10.1904 9.42919 9.91709 9.62353C9.60871 9.8428 9.24209 9.98381 8.50886 10.2658L4 12L8.50886 13.7342C9.24209 14.0162 9.60871 14.1572 9.91709 14.3765C10.1904 14.5708 10.4292 14.8096 10.6235 15.0829C10.8428 15.3913 10.9838 15.7579 11.2658 16.4911L13 21L14.7342 16.4911C15.0162 15.7579 15.1572 15.3913 15.3765 15.0829C15.5708 14.8096 15.8096 14.5708 16.0829 14.3765C16.3913 14.1572 16.7579 14.0162 17.4911 13.7342L22 12L17.4911 10.2658C16.7579 9.98381 16.3913 9.8428 16.0829 9.62353C15.8096 9.42919 15.5708 9.1904 15.3765 8.91709C15.1572 8.60871 15.0162 8.24209 14.7342 7.50886L13 3Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/** Soft-blue strip shown above the expense table when a report contains AI-generated expenses. */
function AiReviewStrip({ aiCount }: { aiCount: number }) {
    if (aiCount === 0) return null;
    return (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm text-blue-800">
            <IconAiSparkle className="h-4 w-4 shrink-0 text-blue-600" />
            <p className="font-semibold">
                {aiCount} expense{aiCount > 1 ? "s were" : " was"} added by AI.
            </p>
        </div>
    );
}

/** Soft-blue strip shown inside the expense drawer when the selected expense is AI-generated. */
function AiDetailStrip() {
    return (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm text-blue-800">
            <IconAiSparkle className="h-4 w-4 shrink-0 text-blue-600" />
            <p className="font-semibold">Added by AI.</p>
        </div>
    );
}

/** AI hint shown below a field (Category or Describe). */
function AiFieldHint({ text }: { text: string }) {
    return <p className="mt-1 text-xs text-blue-600">{text}</p>;
}

// ─────────────────────────────────────────────────────────────────────────────

// Receipt column icon: the warning icon only for "Receipt required"; every other state is the check icon.
function ReceiptIndicatorIcon({ expense }: { expense: ViolationEvaluableExpense }) {
    return getReceiptIndicator(expense).kind === "required" ? <IconReceiptWarning /> : <IconCheckCircle />;
}

function getApproveBlockedTooltip(
    expense: ViolationEvaluableExpense & { category: string; subCategoryId?: number | string | null },
    allCategories: Category[],
    reportStatus: ReportStatus,
): string | undefined {
    if (reportStatus === ReportStatusValue.Approved) return undefined;
    // The category block leads and is never combined with the violation tooltip.
    if (getItemCategoryBlockStatus(expense, allCategories)) return CATEGORY_ITEM_APPROVE_TOOLTIP;
    if (hasBlockingViolation(expense)) return ITEM_VIOLATION_APPROVE_TOOLTIP;
    return undefined;
}

// Props that block the item-level approve action (category first, then policy violations).
function getApproveBlockProps(
    expense: ViolationEvaluableExpense & { category: string; subCategoryId?: number | string | null },
    allCategories: Category[],
    reportStatus: ReportStatus,
): { isApproveBlocked: boolean; blockedTooltip?: string } {
    const blockedTooltip = getApproveBlockedTooltip(expense, allCategories, reportStatus);
    return { isApproveBlocked: !!blockedTooltip, blockedTooltip };
}

// Renders the Exception column indicator, matching WebV5's ReportsList Exception cell:
// a red/amber "Policy Violation" label plus an optional "Explanation required" sub-label.
// Returns null when there are no active violations.
function ExceptionIndicator({ violations }: { violations: PolicyViolation[] | null | undefined }) {
    const active = getNonReceiptViolations(violations);
    if (active.length === 0) return null;

    const requiresExplanation = active.some(violationRequiresExplanation);
    const needsJustification = active.some(violationNeedsJustification);

    return (
        <span className="flex flex-col gap-0.5">
            <span className={`whitespace-nowrap text-xs font-bold ${requiresExplanation ? "text-red-600" : "text-amber-500"}`}>Policy Violation</span>
            {needsJustification && <span className="whitespace-nowrap text-[11px] font-medium text-red-600">Explanation required</span>}
        </span>
    );
}

// Renders the per-violation banner shown in the expense detail panel, matching WebV5's
// EditExpense detail view: one card per active violation, placed just above the Receipt
// section. Read-only — no "Add Explanation" action. Returns null when there are no active
// violations.
function ExpenseDetailViolations({ violations, onViewExplanation }: { violations: PolicyViolation[] | null | undefined; onViewExplanation?: () => void }) {
    const active = getNonReceiptViolations(violations);
    if (active.length === 0) return null;

    return (
        <div className="flex flex-col gap-2">
            {active.map((violation, index) => {
                const requiresExplanation = violationRequiresExplanation(violation);
                return (
                    <Banner
                        key={violation.violationId ?? index}
                        tone={requiresExplanation ? BANNER_TONE.Error : BANNER_TONE.Warning}
                        title={`Policy Violation${violation.policyName ? ` — ${violation.policyName}` : ""}`}
                        description={
                            violationNeedsJustification(violation)
                                ? "Explanation required"
                                : violation.justification?.trim()
                                  ? `Explanation: ${violation.justification.trim()}`
                                  : undefined
                        }
                        action={
                            onViewExplanation && requiresExplanation && violationHasExplanation(violation) ? (
                                <button
                                    type="button"
                                    aria-label={`View Explanation${violation.policyName ? ` for ${violation.policyName}` : ""}`}
                                    className="shrink-0 text-sm font-medium text-red-600 underline"
                                    onClick={onViewExplanation}
                                >
                                    View Explanation
                                </button>
                            ) : undefined
                        }
                    />
                );
            })}
        </div>
    );
}

function IconX() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}

/** Which button slot is active in ExpenseActionButtons */
const enum ButtonSlot {
    Check = "check",
    Deny = "deny",
}

/**
 * Resolve which button slot should show the spinner when the action was
 * triggered externally (e.g. from the drawer) and this instance has no local
 * pendingAction.
 *
 * - "approve" always maps to the check (✓) button.
 * - "deny"    always maps to the deny (✗) button.
 * - "pending" is a revert: the button that was previously filled (i.e. the one
 *   matching the item's current status before the mutation) is the one the user
 *   clicked, so we derive the slot from itemStatus:
 *     APPROVED → user clicked check → show spinner on check
 *     REJECTED → user clicked deny  → show spinner on deny
 */
function resolveExternalButtonSlot(processingAction: ExpenseAction, itemStatus: ItemStatus): ButtonSlot {
    if (processingAction === ExpenseAction.Approve) return ButtonSlot.Check;
    if (processingAction === ExpenseAction.Deny) return ButtonSlot.Deny;
    // Pending revert — derive from the status that is being reverted away from
    return itemStatus === ItemStatusValue.Approved ? ButtonSlot.Check : ButtonSlot.Deny;
}

function ExpenseActionButtons({
    itemId,
    itemStatus,
    isDisabled,
    isProcessing,
    processingAction,
    hasUnsavedChanges,
    isApproveBlocked = false,
    blockedTooltip = CATEGORY_ITEM_APPROVE_TOOLTIP,
    onApprove,
    onDeny,
    onPending,
}: {
    itemId: string;
    itemStatus: ItemStatus;
    isDisabled: boolean;
    isProcessing: boolean;
    processingAction?: ExpenseAction | null;
    hasUnsavedChanges?: boolean;
    /** Blocks the approve (check) action, e.g. while the expense's category is deactivated or it has a blocking violation. */
    isApproveBlocked?: boolean;
    /** Explains why the approve action is blocked; defaults to the category message. */
    blockedTooltip?: string;
    onApprove: (id: string) => void;
    onDeny: (id: string) => void;
    onPending: (id: string) => void;
}) {
    // Local pending slot when user clicks within this instance.
    const [localSlot, setLocalSlot] = useState<ButtonSlot | null>(null);
    const canWrite = useApprovalWriteAccess();

    useEffect(() => {
        if (!isProcessing) setLocalSlot(null);
    }, [isProcessing]);

    const handleCheck = () => {
        setLocalSlot(ButtonSlot.Check);
        const isApproved = itemStatus === ItemStatusValue.Approved;
        if (isApproved) {
            onPending(itemId);
        } else {
            onApprove(itemId);
        }
    };

    const handleDeny = () => {
        setLocalSlot(ButtonSlot.Deny);
        const isDenied = itemStatus === ItemStatusValue.Rejected;
        if (isDenied) {
            onPending(itemId);
        } else {
            onDeny(itemId);
        }
    };

    // Local click takes precedence; fall back to externally-derived slot when processing.
    const activeSlot: ButtonSlot | null = localSlot ?? (isProcessing && processingAction ? resolveExternalButtonSlot(processingAction!, itemStatus) : null);

    const showCheckSpinner = isProcessing && activeSlot === ButtonSlot.Check;
    const showDenySpinner = isProcessing && activeSlot === ButtonSlot.Deny;

    const checkTooltip = hasUnsavedChanges ? "Save your changes first" : itemStatus === ItemStatusValue.Approved ? "Revert to pending" : "Approve";
    const denyTooltip = hasUnsavedChanges ? "Save your changes first" : itemStatus === ItemStatusValue.Rejected ? "Revert to pending" : "Clarify";

    const checkIcon = showCheckSpinner ? (
        <SpinnerIcon className="h-3.5 w-3.5 text-current" />
    ) : (
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
    );

    const denyIcon = showDenySpinner ? (
        <SpinnerIcon className="h-3.5 w-3.5 text-current" />
    ) : (
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
    );

    const isApproved = itemStatus === ItemStatusValue.Approved;
    const isDenied = itemStatus === ItemStatusValue.Rejected;
    // Reverting an already-approved item is still allowed; only a new approval is blocked.
    const isCheckBlocked = isApproveBlocked && !isApproved;
    const isHardDisabled = isDisabled || !!hasUnsavedChanges || !canWrite;
    const checkBaseClass = "h-7 w-7 rounded border p-0 shadow-none ring-0 disabled:opacity-50 disabled:shadow-none";

    return (
        <div className="flex items-center gap-1">
            {/* A prop-disabled button also disables its tooltip, so the blocked state is only styled and made inert. */}
            {isCheckBlocked && !isHardDisabled ? (
                <BlockedActionTooltip title={blockedTooltip}>
                    {(reveal) => (
                        <ButtonUtility
                            aria-label={blockedTooltip}
                            aria-disabled="true"
                            icon={checkIcon}
                            onClick={reveal}
                            className={`${checkBaseClass} cursor-not-allowed border-green-500 bg-white text-green-600 opacity-50 hover:bg-white`}
                        />
                    )}
                </BlockedActionTooltip>
            ) : (
                <ButtonUtility
                    tooltip={checkTooltip}
                    isDisabled={isHardDisabled}
                    icon={checkIcon}
                    onClick={handleCheck}
                    className={`${checkBaseClass} ${
                        isApproved
                            ? "border-green-600 bg-green-600 text-white hover:bg-green-700"
                            : "border-green-500 bg-white text-green-600 hover:bg-green-50"
                    }`}
                />
            )}
            <ButtonUtility
                tooltip={denyTooltip}
                isDisabled={isHardDisabled}
                icon={denyIcon}
                onClick={handleDeny}
                className={`h-7 w-7 rounded border p-0 shadow-none ring-0 disabled:opacity-50 disabled:shadow-none ${
                    isDenied ? "border-red-500 bg-red-500 text-white hover:bg-red-600" : "border-red-400 bg-white text-red-500 hover:bg-red-50"
                }`}
            />
        </div>
    );
}

type ColumnSearchHistory = Record<string, string[]>;

function normalizeColumnFilterValue(value: string) {
    return value.trim();
}

function loadColumnSearchHistory(storageKey: string): ColumnSearchHistory {
    if (typeof window === "undefined") return {};

    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) return {};

        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const next: ColumnSearchHistory = {};

        for (const [column, values] of Object.entries(parsed)) {
            if (!Array.isArray(values)) continue;

            const normalizedValues = values
                .filter((value): value is string => typeof value === "string")
                .map((value) => normalizeColumnFilterValue(value))
                .filter((value, index, all) => value.length > 0 && all.indexOf(value) === index);

            if (normalizedValues.length > 0) {
                next[column] = normalizedValues;
            }
        }

        return next;
    } catch {
        return {};
    }
}

function saveColumnSearchHistory(storageKey: string, history: ColumnSearchHistory) {
    if (typeof window === "undefined") return;

    try {
        window.localStorage.setItem(storageKey, JSON.stringify(history));
    } catch {
        // Ignore localStorage write errors (for example, private mode quota limits).
    }
}

function addSearchValueToHistory(history: ColumnSearchHistory, columnKey: string, value: string): ColumnSearchHistory {
    const normalizedValue = normalizeColumnFilterValue(value);
    if (!normalizedValue) return history;

    const previousValues = history[columnKey] ?? [];
    const deduped = previousValues.filter((previousValue) => previousValue !== normalizedValue);

    return {
        ...history,
        [columnKey]: [normalizedValue, ...deduped],
    };
}

function removeSearchValueFromHistory(history: ColumnSearchHistory, columnKey: string, value: string): ColumnSearchHistory {
    const normalizedValue = normalizeColumnFilterValue(value);
    const previousValues = history[columnKey] ?? [];
    const nextValues = previousValues.filter((previousValue) => previousValue !== normalizedValue);

    if (nextValues.length === 0) {
        const { [columnKey]: _removed, ...rest } = history;
        return rest;
    }

    return {
        ...history,
        [columnKey]: nextValues,
    };
}

const COLUMN_FILTER_POPOVER_WIDTH = 288;
const COLUMN_FILTER_VIEWPORT_GUTTER = 16;

// ─── Number range popover (Items and Total columns — two plain number inputs) ─

function AmountRangeFilterPopover({
    isOpen,
    anchorEl,
    columnLabel,
    value,
    onChange,
    onApply,
    onClear,
    onClose,
    minLabel = "Min ($)",
    maxLabel = "Max ($)",
    step,
}: {
    isOpen: boolean;
    anchorEl: HTMLElement | null;
    columnLabel: string;
    value: { min: string; max: string };
    minLabel?: string;
    maxLabel?: string;
    step?: number;
    onChange: (value: { min: string; max: string }) => void;
    onApply: () => void;
    onClear: () => void;
    onClose: () => void;
}) {
    const baseId = useId();
    const minInputId = `${baseId}-min`;
    const maxInputId = `${baseId}-max`;
    const [position, setPosition] = useState({ top: 0, left: 0, width: COLUMN_FILTER_POPOVER_WIDTH });
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen || !anchorEl) return;
        const update = () => {
            const rect = anchorEl.getBoundingClientRect();
            const vw = window.innerWidth || document.documentElement.clientWidth;
            const width = Math.min(COLUMN_FILTER_POPOVER_WIDTH, Math.max(0, vw - COLUMN_FILTER_VIEWPORT_GUTTER * 2));
            const maxLeft = Math.max(COLUMN_FILTER_VIEWPORT_GUTTER, vw - width - COLUMN_FILTER_VIEWPORT_GUTTER);
            const left = Math.min(Math.max(rect.left, COLUMN_FILTER_VIEWPORT_GUTTER), maxLeft);
            setPosition({ top: rect.bottom + 8, left, width });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [isOpen, anchorEl]);

    useEffect(() => {
        if (!isOpen) return;
        const handlePointerDown = (e: PointerEvent) => {
            const target = e.target as Node;
            if (popoverRef.current && !popoverRef.current.contains(target) && !anchorEl?.contains(target)) {
                onClose();
            }
        };
        document.addEventListener("pointerdown", handlePointerDown);
        return () => document.removeEventListener("pointerdown", handlePointerDown);
    }, [isOpen, anchorEl, onClose]);

    if (!isOpen || !anchorEl || typeof document === "undefined") return null;

    const minVal = parseFloat(value.min);
    const maxVal = parseFloat(value.max);
    const isEmpty = value.min === "" && value.max === "";
    const rangeError = value.min !== "" && value.max !== "" && !isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal;

    const handleKey = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (!rangeError && !isEmpty) onApply();
        }
        if (e.key === "Escape") onClose();
    };

    return createPortal(
        <div
            ref={popoverRef}
            className="fixed z-[120] rounded-lg border border-gray-300 bg-white shadow-lg"
            style={{ top: position.top, left: position.left, width: position.width }}
        >
            <div className="p-4">
                <p className="mb-3 text-sm font-medium text-gray-700">{columnLabel}</p>
                <div className="flex gap-3">
                    <div className="flex-1">
                        <label htmlFor={minInputId} className="mb-1 block text-xs text-gray-400">
                            {minLabel}
                        </label>
                        <input
                            id={minInputId}
                            autoFocus
                            type="number"
                            min={0}
                            step={step}
                            placeholder="0"
                            value={value.min}
                            onChange={(e) => onChange({ ...value, min: e.target.value })}
                            onKeyDown={handleKey}
                            className={`w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-blue-500 ${rangeError ? "border-red-400" : "border-gray-300"}`}
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor={maxInputId} className="mb-1 block text-xs text-gray-400">
                            {maxLabel}
                        </label>
                        <input
                            id={maxInputId}
                            type="number"
                            min={0}
                            step={step}
                            placeholder="No limit"
                            value={value.max}
                            onChange={(e) => onChange({ ...value, max: e.target.value })}
                            onKeyDown={handleKey}
                            className={`w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-blue-500 ${rangeError ? "border-red-400" : "border-gray-300"}`}
                        />
                    </div>
                </div>
                {rangeError && <p className="mt-2 text-xs text-red-500">Max must be greater than or equal to Min</p>}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-3 py-2">
                <button type="button" className="rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100" onClick={onClear}>
                    Clear
                </button>
                <button
                    type="button"
                    disabled={rangeError || isEmpty}
                    className="rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={onApply}
                >
                    Apply
                </button>
            </div>
        </div>,
        document.body,
    );
}

function IconCalendar() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M3 10h18M8 2v4M16 2v4" strokeLinecap="round" />
        </svg>
    );
}

// ─── Confirm Approve Modal ────────────────────────────────────────────────────

function ConfirmApproveModal({ onConfirm, onCancel, isProcessing }: { onConfirm: () => void; onCancel: () => void; isProcessing: boolean }) {
    const titleId = useId();
    const previousFocusRef = useRef<HTMLElement | null>(document.activeElement as HTMLElement);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !isProcessing) onCancel();
        };

        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [onCancel, isProcessing]);

    useEffect(() => {
        return () => {
            previousFocusRef.current?.focus();
        };
    }, []);

    const handleConfirm = () => {
        setSubmitted(true);
        onConfirm();
    };

    return createPortal(
        <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-[60] bg-black/30" onClick={isProcessing ? undefined : onCancel} aria-hidden="true" />

            {/* Dialog */}
            <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                    <h2 id={titleId} className="text-lg font-semibold text-gray-900">
                        Approve Report
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">Are you sure you want to approve this report? All pending expenses will be marked as approved.</p>
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            type="button"
                            className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-xs hover:bg-gray-50"
                            onClick={onCancel}
                            disabled={isProcessing}
                            autoFocus
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="rounded-lg bg-brand-solid px-4 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-brand-solid-hover disabled:opacity-50"
                            onClick={handleConfirm}
                            disabled={isProcessing || submitted}
                        >
                            {isProcessing ? "Approving…" : "Approve Report"}
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body,
    );
}

function ConfirmDenyModal({ onConfirm, onCancel, isProcessing }: { onConfirm: () => void; onCancel: () => void; isProcessing: boolean }) {
    const titleId = useId();
    const previousFocusRef = useRef<HTMLElement | null>(document.activeElement as HTMLElement);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !isProcessing) onCancel();
        };

        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [onCancel, isProcessing]);

    useEffect(() => {
        return () => {
            previousFocusRef.current?.focus();
        };
    }, []);

    const handleConfirm = () => {
        setSubmitted(true);
        onConfirm();
    };

    return createPortal(
        <>
            <div className="fixed inset-0 z-[60] bg-black/30" onClick={isProcessing ? undefined : onCancel} aria-hidden="true" />

            <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                    <h2 id={titleId} className="text-lg font-semibold text-gray-900">
                        Send Back Report
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">Send this report back to the submitter for corrections or clarification.</p>
                    <div className="mt-6 flex justify-end gap-3">
                        <button
                            type="button"
                            className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-xs hover:bg-gray-50"
                            onClick={onCancel}
                            disabled={isProcessing}
                            autoFocus
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="rounded-lg bg-brand-solid px-4 py-2.5 text-sm font-semibold text-white shadow-xs hover:bg-brand-solid-hover disabled:opacity-50"
                            onClick={handleConfirm}
                            disabled={isProcessing || submitted}
                        >
                            {isProcessing ? "Sending…" : "Send Back"}
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body,
    );
}

function UpdateExpenseErrorModal({ message, onClose, title = "Update failed" }: { message: string; onClose: () => void; title?: string }) {
    const titleId = useId();
    const previousFocusRef = useRef<HTMLElement | null>(document.activeElement as HTMLElement);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };

        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [onClose]);

    useEffect(() => {
        return () => {
            previousFocusRef.current?.focus();
        };
    }, []);

    return (
        <ModalOverlay
            isOpen
            isDismissable
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4"
        >
            <Modal className="w-full max-w-md">
                <Dialog aria-labelledby={titleId} className="outline-none">
                    <div className="w-full rounded-xl bg-white p-6 shadow-xl">
                        <h2 id={titleId} className="text-lg font-semibold text-gray-900">
                            {title}
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">{message}</p>
                        <div className="mt-6 flex justify-end">
                            <button
                                type="button"
                                className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-xs hover:bg-gray-50"
                                onClick={onClose}
                                autoFocus
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}

// ─── Reports Table ────────────────────────────────────────────────────────────

type ReportSortKey = "submitter" | "status" | "reportNumber" | "itemCount" | "totalAmount";

function buildColumnFilter(
    filters: Partial<Record<ReportSortKey, string>>,
    itemRange: { min: string; max: string } | null,
    amountRange: { min: string; max: string } | null,
): ApproverReportsColumnFilter {
    const result: ApproverReportsColumnFilter = {};
    if (filters.submitter) result.submitterFullNameSearch = filters.submitter;
    if (filters.status) result.state = filters.status as ReportStatus;
    if (filters.reportNumber) result.reportNumber = filters.reportNumber;
    if (itemRange) {
        const min = Math.trunc(parseFloat(itemRange.min));
        const max = Math.trunc(parseFloat(itemRange.max));
        if (!isNaN(min)) result.expensesCountMin = min;
        if (!isNaN(max)) result.expensesCountMax = max;
    }
    if (amountRange) {
        const min = parseFloat(amountRange.min);
        const max = parseFloat(amountRange.max);
        if (!isNaN(min)) result.totalAmountMin = min;
        if (!isNaN(max)) result.totalAmountMax = max;
    }
    return result;
}

function ReportsTable({
    data,
    subTab,
    isLoading,
    isMutating,
    isProcessingReport,
    processingItemId,
    processingAction,
    reportExpensesMap,
    loadReportExpenses,
    pageInfo,
    onPageChange,
    sortKey,
    sortDirection,
    onSort,
    onSelectItem,
    onApproveItem,
    onDenyItem,
    onPendingItem,
    onApproveReport,
    onDenyReport,
    getReportComments,
    onFilter,
    onViewReceipt,
    allCategories,
}: {
    data: ReportsSummary | null;
    subTab: string;
    isLoading: boolean;
    isMutating: boolean;
    isProcessingReport: boolean;
    processingItemId: string | null;
    processingAction: ExpenseAction | null;
    reportExpensesMap: Record<string, ReportExpensesData>;
    loadReportExpenses: (reportId: string) => void;
    pageInfo?: TablePageInfo | null;
    onPageChange?: (page: number) => void;
    sortKey: ReportSortBy | null;
    sortDirection: "asc" | "desc";
    onSort: (key: ReportSortBy) => void;
    onSelectItem: (id: string) => void;
    onApproveItem: (id: string) => void;
    onDenyItem: (id: string) => void;
    onPendingItem: (id: string) => void;
    onApproveReport: (id: string, message?: string) => void;
    onDenyReport: (id: string, message?: string) => void;
    getReportComments?: (reportId: string) => Promise<any>;
    onFilter: (filter: ApproverReportsColumnFilter) => void;
    onViewReceipt: (id: string) => void;
    allCategories: Category[];
}) {
    const [columnFilters, setColumnFilters] = useState<Partial<Record<ReportSortKey, string>>>({});
    const [activeFilterColumn, setActiveFilterColumn] = useState<ReportSortKey | null>(null);
    const [activeFilterAnchor, setActiveFilterAnchor] = useState<HTMLElement | null>(null);
    const [filterInput, setFilterInput] = useState("");
    const [searchHistory, setSearchHistory] = useState<ColumnSearchHistory>({});

    // Range filter states — null means not active (not sent to API)
    const [itemCountRange, setItemCountRange] = useState<{ min: string; max: string } | null>(null);
    const [totalAmountRange, setTotalAmountRange] = useState<{ min: string; max: string } | null>(null);
    // Draft states — local edits before Apply is clicked
    const [draftItemCount, setDraftItemCount] = useState({ min: "", max: "" });
    const [draftTotalAmount, setDraftTotalAmount] = useState({ min: "", max: "" });

    // Sync draft from active filter when a range popover opens
    useEffect(() => {
        if (activeFilterColumn === "itemCount") {
            setDraftItemCount(itemCountRange ?? { min: "", max: "" });
        }
        if (activeFilterColumn === "totalAmount") {
            setDraftTotalAmount(totalAmountRange ?? { min: "", max: "" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilterColumn]);

    const storageKey = REPORTS_COLUMN_SEARCH_HISTORY_KEY;

    useEffect(() => {
        setSearchHistory(loadColumnSearchHistory(storageKey));
    }, []);

    useEffect(() => {
        saveColumnSearchHistory(storageKey, searchHistory);
    }, [searchHistory]);

    const applyColumnFilter = useCallback(() => {
        if (!activeFilterColumn) return;

        const normalizedValue = normalizeColumnFilterValue(filterInput);
        const newColumnFilters = { ...columnFilters };

        if (normalizedValue) {
            newColumnFilters[activeFilterColumn] = normalizedValue;
        } else {
            delete newColumnFilters[activeFilterColumn];
        }

        setColumnFilters(newColumnFilters);

        if (normalizedValue) {
            setSearchHistory((previous) => addSearchValueToHistory(previous, activeFilterColumn, normalizedValue));
        }

        onFilter(buildColumnFilter(newColumnFilters, itemCountRange, totalAmountRange));
        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
    }, [activeFilterColumn, columnFilters, filterInput, itemCountRange, totalAmountRange, onFilter]);

    const clearColumnFilter = useCallback(() => {
        if (!activeFilterColumn) return;
        setFilterInput("");
        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
        if (!(activeFilterColumn in columnFilters)) return;
        const newColumnFilters = { ...columnFilters };
        delete newColumnFilters[activeFilterColumn];
        setColumnFilters(newColumnFilters);
        onFilter(buildColumnFilter(newColumnFilters, itemCountRange, totalAmountRange));
    }, [activeFilterColumn, columnFilters, itemCountRange, totalAmountRange, onFilter]);

    const removeSavedSearchValue = useCallback(
        (value: string) => {
            if (!activeFilterColumn) return;
            setSearchHistory((previous) => removeSearchValueFromHistory(previous, activeFilterColumn, value));
        },
        [activeFilterColumn],
    );

    const applyItemCountFilter = useCallback(() => {
        setItemCountRange(draftItemCount);
        onFilter(buildColumnFilter(columnFilters, draftItemCount, totalAmountRange));
        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
    }, [draftItemCount, columnFilters, totalAmountRange, onFilter]);

    const clearItemCountFilter = useCallback(() => {
        setDraftItemCount({ min: "", max: "" });
        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
        if (itemCountRange === null) return;
        setItemCountRange(null);
        onFilter(buildColumnFilter(columnFilters, null, totalAmountRange));
    }, [itemCountRange, columnFilters, totalAmountRange, onFilter]);

    const applyTotalAmountFilter = useCallback(() => {
        setTotalAmountRange(draftTotalAmount);
        onFilter(buildColumnFilter(columnFilters, itemCountRange, draftTotalAmount));
        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
    }, [draftTotalAmount, columnFilters, itemCountRange, onFilter]);

    const clearTotalAmountFilter = useCallback(() => {
        setDraftTotalAmount({ min: "", max: "" });
        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
        if (totalAmountRange === null) return;
        setTotalAmountRange(null);
        onFilter(buildColumnFilter(columnFilters, itemCountRange, null));
    }, [totalAmountRange, columnFilters, itemCountRange, onFilter]);

    const openColumnFilter = useCallback(
        (column: ReportSortKey, columnLabel: string, anchorEl: HTMLElement) => {
            if (activeFilterColumn === column) {
                setActiveFilterColumn(null);
                setActiveFilterAnchor(null);
                return;
            }

            setActiveFilterColumn(column);
            setActiveFilterAnchor(anchorEl);
            setFilterInput(columnFilters[column] ?? "");
            // Keep this label in closure for a11y debugging consistency.
            void columnLabel;
        },
        [activeFilterColumn, columnFilters],
    );

    const handleSort = useCallback(
        (key: ReportSortKey) => {
            const sortByMap: Record<ReportSortKey, ReportSortBy> = {
                submitter: "submitterName",
                status: "status",
                reportNumber: "reportNumber",
                itemCount: "itemCount",
                totalAmount: "totalAmount",
            };
            onSort(sortByMap[key]);
        },
        [onSort],
    );

    // Reverse-map the API sortBy value back to the column key for active sort indicator in the header.
    const activeSortColumn = useMemo<ReportSortKey | null>(() => {
        if (!sortKey) return null;
        const reverseMap: Record<ReportSortBy, ReportSortKey> = {
            submitterName: "submitter",
            status: "status",
            reportNumber: "reportNumber",
            itemCount: "itemCount",
            totalAmount: "totalAmount",
        };
        return reverseMap[sortKey];
    }, [sortKey]);

    const filteredReports = data?.reports ?? [];

    if (!data) {
        return <DataTableLoading tabName="reports" description="Please wait while the expense reports load." className="mt-6" />;
    }

    return (
        <div>
            {/* Mobile card list — shown on screens smaller than md */}
            <div className="relative divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white md:hidden">
                <TableLoadingOverlay isLoading={isLoading} label={tableLoadingLabel("reports")} />
                {filteredReports.map((report) => (
                    <ReportMobileCard
                        key={report.id}
                        report={report}
                        isMutating={isMutating}
                        isProcessingReport={isProcessingReport}
                        processingItemId={processingItemId}
                        processingAction={processingAction}
                        reportExpensesMap={reportExpensesMap}
                        loadReportExpenses={loadReportExpenses}
                        onSelectItem={onSelectItem}
                        onApproveItem={onApproveItem}
                        onDenyItem={onDenyItem}
                        onPendingItem={onPendingItem}
                        onApproveReport={onApproveReport}
                        onDenyReport={onDenyReport}
                        getReportComments={getReportComments}
                        onViewReceipt={onViewReceipt}
                        allCategories={allCategories}
                    />
                ))}
                {filteredReports.length === 0 && (
                    <p className="flex min-h-40 items-center justify-center px-4 py-8 text-center text-sm text-gray-400">No reports found.</p>
                )}
            </div>

            {/* Desktop table — hidden on mobile */}
            <div className="relative hidden rounded-xl border border-gray-200 bg-white md:block">
                <TableLoadingOverlay isLoading={isLoading} label={tableLoadingLabel("reports")} />
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[880px] text-sm">
                        <thead className="relative z-30">
                            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                                {[
                                    <ColumnHeader
                                        key="submitter-header"
                                        label="Submitter"
                                        isFilterable
                                        isFilterActive={Boolean(columnFilters.submitter)}
                                        isFilterOpen={activeFilterColumn === "submitter"}
                                        onFilterPress={(anchorEl) => openColumnFilter("submitter", "Submitter", anchorEl)}
                                        isSortable
                                        isSortActive={activeSortColumn === "submitter"}
                                        sortDirection={sortDirection}
                                        onSort={() => handleSort("submitter")}
                                    />,
                                    <ColumnFilterPopover
                                        key="submitter-filter"
                                        isOpen={activeFilterColumn === "submitter"}
                                        anchorEl={activeFilterAnchor}
                                        columnLabel="Submitter"
                                        searchValue={filterInput}
                                        savedValues={searchHistory.submitter ?? []}
                                        onSearchChange={setFilterInput}
                                        onApply={applyColumnFilter}
                                        onClear={clearColumnFilter}
                                        onClose={() => {
                                            setActiveFilterColumn(null);
                                            setActiveFilterAnchor(null);
                                        }}
                                        onSelectValue={(value) => {
                                            const newColumnFilters = { ...columnFilters, submitter: value };
                                            setFilterInput(value);
                                            setColumnFilters(newColumnFilters);
                                            setSearchHistory((previous) => addSearchValueToHistory(previous, "submitter", value));
                                            onFilter(buildColumnFilter(newColumnFilters, itemCountRange, totalAmountRange));
                                            setActiveFilterColumn(null);
                                            setActiveFilterAnchor(null);
                                        }}
                                        onRemoveSavedValue={removeSavedSearchValue}
                                    />,
                                ]}
                                {[
                                    <ColumnHeader
                                        key="status-header"
                                        label="Status"
                                        isFilterable
                                        isFilterActive={Boolean(columnFilters.status)}
                                        isFilterOpen={activeFilterColumn === "status"}
                                        onFilterPress={(anchorEl) => openColumnFilter("status", "Status", anchorEl)}
                                        isSortable
                                        isSortActive={activeSortColumn === "status"}
                                        sortDirection={sortDirection}
                                        onSort={() => handleSort("status")}
                                    />,
                                    <ColumnFilterPopover
                                        key="status-filter"
                                        isOpen={activeFilterColumn === "status"}
                                        anchorEl={activeFilterAnchor}
                                        columnLabel="Status"
                                        options={STATUS_FILTER_OPTIONS[subTab as ActiveSubTab] ?? STATUS_FILTER_OPTIONS[SUB_TAB.Review]}
                                        searchValue={filterInput}
                                        onSearchChange={setFilterInput}
                                        onApply={applyColumnFilter}
                                        onClear={clearColumnFilter}
                                        onClose={() => {
                                            setActiveFilterColumn(null);
                                            setActiveFilterAnchor(null);
                                        }}
                                    />,
                                ]}
                                {[
                                    <ColumnHeader
                                        key="report-number-header"
                                        label="Report ID"
                                        isFilterable
                                        isFilterActive={Boolean(columnFilters.reportNumber)}
                                        isFilterOpen={activeFilterColumn === "reportNumber"}
                                        onFilterPress={(anchorEl) => openColumnFilter("reportNumber", "Report ID", anchorEl)}
                                        isSortable
                                        isSortActive={activeSortColumn === "reportNumber"}
                                        sortDirection={sortDirection}
                                        onSort={() => handleSort("reportNumber")}
                                    />,
                                    <ColumnFilterPopover
                                        key="report-number-filter"
                                        isOpen={activeFilterColumn === "reportNumber"}
                                        anchorEl={activeFilterAnchor}
                                        columnLabel="Report ID"
                                        searchValue={filterInput}
                                        savedValues={searchHistory.reportNumber ?? []}
                                        onSearchChange={setFilterInput}
                                        onApply={applyColumnFilter}
                                        onClear={clearColumnFilter}
                                        onClose={() => {
                                            setActiveFilterColumn(null);
                                            setActiveFilterAnchor(null);
                                        }}
                                        onSelectValue={(value) => {
                                            const newColumnFilters = { ...columnFilters, reportNumber: value };
                                            setFilterInput(value);
                                            setColumnFilters(newColumnFilters);
                                            setSearchHistory((previous) => addSearchValueToHistory(previous, "reportNumber", value));
                                            onFilter(buildColumnFilter(newColumnFilters, itemCountRange, totalAmountRange));
                                            setActiveFilterColumn(null);
                                            setActiveFilterAnchor(null);
                                        }}
                                        onRemoveSavedValue={removeSavedSearchValue}
                                    />,
                                ]}
                                {[
                                    <ColumnHeader
                                        key="item-count-header"
                                        label="Items"
                                        className="w-20 px-4 py-3"
                                        isFilterable
                                        isFilterActive={itemCountRange !== null}
                                        isFilterOpen={activeFilterColumn === "itemCount"}
                                        onFilterPress={(anchorEl) => openColumnFilter("itemCount", "Items", anchorEl)}
                                        isSortable
                                        isSortActive={activeSortColumn === "itemCount"}
                                        sortDirection={sortDirection}
                                        onSort={() => handleSort("itemCount")}
                                    />,
                                    <AmountRangeFilterPopover
                                        key="item-count-filter"
                                        isOpen={activeFilterColumn === "itemCount"}
                                        anchorEl={activeFilterAnchor}
                                        columnLabel="Items"
                                        minLabel="Min"
                                        maxLabel="Max"
                                        step={1}
                                        value={draftItemCount}
                                        onChange={setDraftItemCount}
                                        onApply={applyItemCountFilter}
                                        onClear={clearItemCountFilter}
                                        onClose={() => {
                                            setActiveFilterColumn(null);
                                            setActiveFilterAnchor(null);
                                        }}
                                    />,
                                ]}
                                {[
                                    <ColumnHeader
                                        key="total-amount-header"
                                        label="Total"
                                        className="w-44 px-4 py-3"
                                        align="right"
                                        isFilterable
                                        isFilterActive={totalAmountRange !== null}
                                        isFilterOpen={activeFilterColumn === "totalAmount"}
                                        onFilterPress={(anchorEl) => openColumnFilter("totalAmount", "Total", anchorEl)}
                                        isSortable
                                        isSortActive={activeSortColumn === "totalAmount"}
                                        sortDirection={sortDirection}
                                        onSort={() => handleSort("totalAmount")}
                                    />,
                                    <AmountRangeFilterPopover
                                        key="total-amount-filter"
                                        isOpen={activeFilterColumn === "totalAmount"}
                                        anchorEl={activeFilterAnchor}
                                        columnLabel="Total Amount"
                                        value={draftTotalAmount}
                                        onChange={setDraftTotalAmount}
                                        onApply={applyTotalAmountFilter}
                                        onClear={clearTotalAmountFilter}
                                        onClose={() => {
                                            setActiveFilterColumn(null);
                                            setActiveFilterAnchor(null);
                                        }}
                                    />,
                                ]}
                                <th className="w-8 px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody className="relative z-10">
                            {filteredReports.map((report: ExpenseReport) => (
                                <ReportRow
                                    key={report.id}
                                    report={report}
                                    isMutating={isMutating}
                                    isProcessingReport={isProcessingReport}
                                    processingItemId={processingItemId}
                                    processingAction={processingAction}
                                    reportExpensesMap={reportExpensesMap}
                                    loadReportExpenses={loadReportExpenses}
                                    onSelectItem={onSelectItem}
                                    onApproveItem={onApproveItem}
                                    onDenyItem={onDenyItem}
                                    onPendingItem={onPendingItem}
                                    onApproveReport={onApproveReport}
                                    onDenyReport={onDenyReport}
                                    getReportComments={getReportComments}
                                    onViewReceipt={onViewReceipt}
                                    allCategories={allCategories}
                                />
                            ))}
                            {filteredReports.length === 0 && (
                                <tr>
                                    <td className="h-40 px-4 text-center align-middle text-sm text-gray-400" colSpan={6}>
                                        No reports found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {pageInfo && onPageChange && <DataTablePagination pageInfo={pageInfo} onPageChange={onPageChange} isLoadingPage={isLoading} />}
        </div>
    );
}

function ReportMobileCard({
    report,
    isMutating,
    isProcessingReport,
    processingItemId: processingItemId,
    processingAction,
    reportExpensesMap,
    loadReportExpenses,
    onSelectItem,
    onApproveItem,
    onDenyItem,
    onPendingItem,
    onApproveReport,
    onDenyReport,
    onViewReceipt,
    allCategories,
    getReportComments,
}: {
    report: ExpenseReport;
    isMutating: boolean;
    isProcessingReport: boolean;
    processingItemId: string | null;
    processingAction: ExpenseAction | null;
    reportExpensesMap: Record<string, ReportExpensesData>;
    loadReportExpenses: (reportId: string) => void;
    onSelectItem: (id: string) => void;
    onApproveItem: (id: string) => void;
    onDenyItem: (id: string) => void;
    onPendingItem: (id: string) => void;
    onApproveReport: (id: string, message?: string) => void;
    onDenyReport: (id: string, message?: string) => void;
    onViewReceipt: (id: string) => void;
    allCategories: Category[];
    getReportComments?: (reportId: string) => Promise<any>;
}) {
    const [expanded, setExpanded] = useState(false);
    const canViewExpenses = report.canViewExpenses;
    const isReportEditable = report.isEditable;

    // Fetch expenses when the row is first expanded
    useEffect(() => {
        if (expanded && canViewExpenses) {
            loadReportExpenses(report.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    const reportExpensesData = reportExpensesMap[report.id];
    const isLoadingExpenses = expanded && canViewExpenses && !reportExpensesData;

    const expenseItems: ReportExpenseItem[] = useMemo(
        () => (reportExpensesData?.items ?? []).map((item) => ({ ...item, isEditable: report.isEditable })),
        [reportExpensesData, report.isEditable],
    );
    const reportCanBeApproved = canApproveReport(expenseItems);
    const canWrite = useApprovalWriteAccess();
    const totalAmount = reportExpensesData ? reportExpensesData.summary.totalAmount : report.totalAmount;
    const totalCount = reportExpensesData ? reportExpensesData.summary.totalCount : report.itemCount;
    const currencyCode = reportExpensesData?.summary.currencyCode ?? report.currencyCode;
    const submittedDate = report.submittedAt;
    const submitterName = `${report.submitter.firstName} ${report.submitter.lastName}`.trim();

    const commentTextRef = useRef("");

    const handleDenyWithComment = () => {
        onDenyReport(report.id, commentTextRef.current.trim() || undefined);
    };

    const handleApproveWithComment = () => {
        onApproveReport(report.id, commentTextRef.current.trim() || undefined);
    };

    return (
        <div>
            <div
                className={`flex items-start gap-3 px-4 py-3 ${canViewExpenses ? "cursor-pointer hover:bg-gray-50" : "cursor-default"}`}
                onClick={() => {
                    if (!canViewExpenses) return;
                    const next = !expanded;
                    setExpanded(next);
                }}
            >
                <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-blue-600">{`${report.submitter.firstName} ${report.submitter.lastName}`.trim()}</p>
                    <p className="truncate text-xs text-gray-400">{report.submitter.email}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <StatusBadge status={report.state} />
                        <span className="text-xs text-gray-500">ID {report.reportNumber}</span>
                        <span className="text-xs text-gray-500">· {report.itemCount} items</span>
                    </div>
                </div>
                <div className="shrink-0 text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(report.totalAmount, report.currencyCode)}</p>
                </div>
                {canViewExpenses && (
                    <button
                        type="button"
                        aria-label={expanded ? "Collapse" : "Expand"}
                        className="shrink-0 rounded p-1 text-gray-400 hover:text-gray-700"
                        onClick={(event) => {
                            event.stopPropagation();
                            const next = !expanded;
                            setExpanded(next);
                        }}
                    >
                        <svg
                            className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path strokeLinecap="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                )}
            </div>

            {expanded && canViewExpenses && (
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                    {isLoadingExpenses ? (
                        <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-400">
                            <SpinnerIcon className="h-3.5 w-3.5 text-gray-400" />
                            <span>Loading expenses…</span>
                        </div>
                    ) : (
                        <>
                            <div className="mb-3 rounded-lg bg-white p-3 text-sm text-gray-600">
                                Submitted on {fmtDate(submittedDate)} by {submitterName}.
                            </div>

                            {/* Report-level comments */}
                            <div className="mb-3 rounded-lg bg-white p-3">
                                <h4 className="mb-2 text-sm font-medium text-gray-700">Comments</h4>
                                <ReportCommentsInline
                                    reportId={report.id}
                                    getReportComments={getReportComments}
                                    textRef={commentTextRef}
                                    disabled={report.state === ReportStatusValue.Approved}
                                />
                                {isReportEditable && (
                                    <div className="mt-2 flex items-center justify-end gap-2">
                                        {isProcessingReport && <SpinnerIcon className="h-3.5 w-3.5 text-blue-500" />}
                                        <button
                                            type="button"
                                            disabled={isMutating || !canWrite}
                                            className="rounded border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                            onClick={() => void handleDenyWithComment()}
                                        >
                                            Send Back
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isMutating || !reportCanBeApproved || !canWrite}
                                            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            title={
                                                reportCanBeApproved ? "Approve this report and all pending expenses" : getApprovalDisabledReason(expenseItems)
                                            }
                                            onClick={() => void handleApproveWithComment()}
                                        >
                                            Approve Report
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="mb-3 flex gap-3">
                                <div className="rounded-lg bg-white px-3 py-2 text-center">
                                    <p className="text-xl font-semibold text-gray-900">{totalCount}</p>
                                    <p className="text-xs text-gray-500">Items</p>
                                </div>
                                <div className="rounded-lg bg-white px-3 py-2 text-center">
                                    <p className="text-xl font-semibold text-gray-900">{formatCurrency(totalAmount, currencyCode)}</p>
                                    <p className="text-xs text-gray-500">Total</p>
                                </div>
                            </div>

                            <AiReviewStrip aiCount={expenseItems.filter(isAiGenerated).length} />

                            {expenseItems.length > 0 ? (
                                <div className="flex flex-col gap-2">
                                    {expenseItems.map((item) => {
                                        return (
                                            <div
                                                key={item.id}
                                                className="cursor-pointer rounded-lg border border-gray-200 bg-white p-3 hover:bg-blue-50/30"
                                                onClick={() => onSelectItem(item.id)}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <p className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                                                            {isAiGenerated(item) && (
                                                                <>
                                                                    <IconAiSparkle className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                                                                    <span className="sr-only">AI-generated</span>
                                                                </>
                                                            )}
                                                            {resolveReportExpenseMerchant(item.merchant, item.category, allCategories)}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            <ReportExpenseCategory item={item} allCategories={allCategories} reportStatus={report.state} />
                                                        </p>
                                                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                            {item.lostReceiptDeclaration?.declared ? (
                                                                <span className="text-xs text-blue-600">Lost receipt declared</span>
                                                            ) : item.receiptMissing ? (
                                                                <span className="text-xs text-red-500">Missing receipt</span>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="text-xs text-blue-600 underline"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onViewReceipt(item.id);
                                                                    }}
                                                                >
                                                                    View receipt
                                                                </button>
                                                            )}
                                                            <ExceptionIndicator violations={item.violations} />
                                                        </div>
                                                    </div>
                                                    <div className="shrink-0 text-right">
                                                        <p className="text-sm font-semibold text-gray-900">{formatCurrency(item.amount, item.currencyCode)}</p>
                                                        <p className="text-xs text-gray-500">{fmtDate(item.settleDate)}</p>
                                                    </div>
                                                </div>
                                                <div className="mt-2 flex items-center justify-end gap-2" onClick={(event) => event.stopPropagation()}>
                                                    <ExpenseActionButtons
                                                        itemId={item.id}
                                                        itemStatus={item.status}
                                                        isDisabled={isMutating || !item.isEditable}
                                                        isProcessing={processingItemId === item.id}
                                                        processingAction={processingItemId === item.id ? processingAction : null}
                                                        {...getApproveBlockProps(item, allCategories, report.state)}
                                                        onApprove={onApproveItem}
                                                        onDeny={onDenyItem}
                                                        onPending={onPendingItem}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="py-4 text-center text-sm text-gray-400">No expense items found</p>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function ReportRow({
    report,
    isMutating,
    isProcessingReport,
    processingItemId,
    processingAction,
    reportExpensesMap,
    loadReportExpenses,
    onSelectItem,
    onApproveItem,
    onDenyItem,
    onPendingItem,
    onApproveReport,
    onDenyReport,
    getReportComments,
    onViewReceipt,
    allCategories,
}: {
    report: ExpenseReport;
    isMutating: boolean;
    isProcessingReport: boolean;
    processingItemId: string | null;
    processingAction: ExpenseAction | null;
    reportExpensesMap: Record<string, ReportExpensesData>;
    loadReportExpenses: (reportId: string) => void;
    onSelectItem: (id: string) => void;
    onApproveItem: (id: string) => void;
    onDenyItem: (id: string) => void;
    onPendingItem: (id: string) => void;
    onApproveReport: (id: string, message?: string) => void;
    onDenyReport: (id: string, message?: string) => void;
    getReportComments?: (reportId: string) => Promise<any>;
    onViewReceipt: (id: string) => void;
    allCategories: Category[];
}) {
    const [expanded, setExpanded] = useState(false);
    const canViewExpenses = report.canViewExpenses;
    const isReportEditable = report.isEditable;

    // Fetch expenses when the row is first expanded
    useEffect(() => {
        if (expanded && canViewExpenses) {
            loadReportExpenses(report.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded]);

    const reportExpensesData = reportExpensesMap[report.id];
    const isLoadingExpenses = expanded && canViewExpenses && !reportExpensesData;

    const expenseItems: ReportExpenseItem[] = useMemo(
        () => (reportExpensesData?.items ?? []).map((item) => ({ ...item, isEditable: report.isEditable })),
        [reportExpensesData, report.isEditable],
    );
    const reportCanBeApproved = canApproveReport(expenseItems);
    const canWrite = useApprovalWriteAccess();
    const totalItems = reportExpensesData ? reportExpensesData.summary.totalCount : report.itemCount;
    const totalAmount = reportExpensesData ? reportExpensesData.summary.totalAmount : report.totalAmount;
    const currencyCode = reportExpensesData?.summary.currencyCode ?? report.currencyCode;

    const commentTextRef = useRef("");

    const handleDenyWithComment = () => {
        onDenyReport(report.id, commentTextRef.current.trim() || undefined);
    };

    const handleApproveWithComment = () => {
        onApproveReport(report.id, commentTextRef.current.trim() || undefined);
    };

    return (
        <>
            <tr
                className={`border-b border-gray-100 ${canViewExpenses ? "cursor-pointer hover:bg-gray-50" : "cursor-default"}`}
                onClick={() => {
                    if (!canViewExpenses) return;
                    const next = !expanded;
                    setExpanded(next);
                }}
            >
                <td className="px-4 py-3">
                    <p className="font-medium text-blue-600">{`${report.submitter.firstName} ${report.submitter.lastName}`.trim()}</p>
                    <p className="text-xs text-gray-400">{report.submitter.email}</p>
                </td>
                <td className="px-4 py-3">
                    <StatusBadge status={report.state} />
                </td>
                <td className="px-4 py-3 text-gray-700">{report.reportNumber}</td>
                <td className="px-4 py-3 text-gray-700">{report.itemCount}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-900">{formatCurrency(report.totalAmount, report.currencyCode)}</td>
                <td className="px-4 py-3">
                    {canViewExpenses && (
                        <button
                            type="button"
                            aria-label={expanded ? "Collapse" : "Expand"}
                            className="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:text-gray-700"
                            onClick={(event) => {
                                event.stopPropagation();
                                const next = !expanded;
                                setExpanded(next);
                            }}
                        >
                            <svg
                                className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path strokeLinecap="round" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    )}
                </td>
            </tr>

            {expanded && canViewExpenses && (
                <tr className="border-b border-gray-100 bg-white">
                    <td colSpan={6} className="px-6 py-5">
                        {/* Report Summary header */}
                        <div className="mb-4 border-b border-gray-100 pb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Report Summary</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Submitted on {fmtDate(report.submittedAt)} by {`${report.submitter.firstName} ${report.submitter.lastName}`.trim()}.
                            </p>
                        </div>

                        {/* Comments section — mounted as soon as the row expands */}
                        <div className="mb-5">
                            <p className="mb-3 text-sm font-semibold text-gray-700">Comments</p>
                            <ReportCommentsInline
                                reportId={report.id}
                                getReportComments={getReportComments}
                                textRef={commentTextRef}
                                disabled={report.state === ReportStatusValue.Approved}
                            />
                            {isReportEditable && (
                                <div className="mt-2 flex items-center justify-end gap-2">
                                    {isProcessingReport && <SpinnerIcon className="h-3.5 w-3.5 text-blue-500" />}
                                    <button
                                        type="button"
                                        disabled={isMutating || !canWrite}
                                        className="rounded border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                        onClick={() => void handleDenyWithComment()}
                                    >
                                        Send Back
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isMutating || !reportCanBeApproved || !canWrite}
                                        className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        title={reportCanBeApproved ? "Approve this report and all pending expenses" : getApprovalDisabledReason(expenseItems)}
                                        onClick={() => void handleApproveWithComment()}
                                    >
                                        Approve Report
                                    </button>
                                </div>
                            )}
                        </div>

                        {isLoadingExpenses ? (
                            <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-400">
                                <SpinnerIcon className="h-3.5 w-3.5 text-gray-400" />
                                <span>Loading expenses…</span>
                            </div>
                        ) : (
                            <>
                                <div className="mb-4 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                                    <div className="w-full rounded-lg bg-white p-3 text-center sm:w-auto sm:min-w-[140px]">
                                        <p className="text-3xl font-semibold text-gray-900">{totalItems}</p>
                                        <p className="text-xs text-gray-500">Total Items</p>
                                    </div>
                                    <div className="w-full rounded-lg bg-white p-3 text-center sm:w-auto sm:min-w-[220px]">
                                        <p className="text-4xl font-semibold text-gray-900">{formatCurrency(totalAmount, currencyCode)}</p>
                                        <p className="text-xs text-gray-500">Total Amount ({currencyCode ?? "USD"})</p>
                                    </div>
                                </div>

                                <AiReviewStrip aiCount={expenseItems.filter(isAiGenerated).length} />

                                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                                    <table className="w-full min-w-[680px] text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                                                <th className="px-3 py-2">Action</th>
                                                <th className="px-3 py-2">Category</th>
                                                <th className="px-3 py-2">Merchant</th>
                                                <th className="px-3 py-2">Receipt</th>
                                                <th className="px-3 py-2">Settle Date</th>
                                                <th className="px-3 py-2">Exception</th>
                                                <th className="px-3 py-2">Last 4</th>
                                                <th className="px-3 py-2 text-right">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {expenseItems.map((item) => {
                                                return (
                                                    <tr
                                                        key={item.id}
                                                        className="cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-blue-50/30"
                                                        onClick={() => onSelectItem(item.id)}
                                                    >
                                                        <td className="px-3 py-2" onClick={(event) => event.stopPropagation()}>
                                                            <ExpenseActionButtons
                                                                itemId={item.id}
                                                                itemStatus={item.status}
                                                                isDisabled={isMutating || !item.isEditable}
                                                                isProcessing={processingItemId === item.id}
                                                                processingAction={processingItemId === item.id ? processingAction : null}
                                                                {...getApproveBlockProps(item, allCategories, report.state)}
                                                                onApprove={onApproveItem}
                                                                onDeny={onDenyItem}
                                                                onPending={onPendingItem}
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <ReportExpenseCategory item={item} allCategories={allCategories} reportStatus={report.state} />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <span className="flex items-center gap-1.5">
                                                                {isAiGenerated(item) && (
                                                                    <>
                                                                        <IconAiSparkle className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                                                                        <span className="sr-only">AI-generated</span>
                                                                    </>
                                                                )}
                                                                {resolveReportExpenseMerchant(item.merchant, item.category, allCategories)}
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                type="button"
                                                                className="cursor-pointer disabled:cursor-default"
                                                                disabled={item.receiptMissing}
                                                                onClick={() => onViewReceipt(item.id)}
                                                                title={getReceiptIndicator(item).label}
                                                                aria-label={getReceiptIndicator(item).label}
                                                            >
                                                                <ReceiptIndicatorIcon expense={item} />
                                                            </button>
                                                        </td>
                                                        <td className="px-3 py-2">{fmtDate(item.settleDate)}</td>
                                                        <td className="px-3 py-2">
                                                            {getNonReceiptViolations(item.violations).length > 0 ? (
                                                                <ExceptionIndicator violations={item.violations} />
                                                            ) : (
                                                                <span className="text-gray-300">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {item.paymentMethod?.method === PAYMENT_METHOD.CreditCard
                                                                ? item.paymentMethod.display.last4
                                                                : (item.paymentMethod?.method ?? "—")}
                                                        </td>
                                                        <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.amount, item.currencyCode)}</td>
                                                    </tr>
                                                );
                                            })}
                                            {expenseItems.length === 0 && (
                                                <tr>
                                                    <td colSpan={8} className="px-3 py-4 text-center text-gray-400">
                                                        No expense items found for this report
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}

// ─── Items Table ──────────────────────────────────────────────────────────────

function ItemsTable({
    data,
    subTab,
    isLoading,
    isMutating,
    processingItemId,
    processingAction,
    onApproveItem,
    onApproveItems,
    onDenyItem,
    onDenyItems,
    onPendingItem,
    onSelectItem,
    onViewReceipt,
    allCategories,
}: {
    data: ItemsSummary | null;
    subTab: string;
    isLoading: boolean;
    isMutating: boolean;
    processingItemId: string | null;
    processingAction: ExpenseAction | null;
    onApproveItem: (id: string) => void;
    onApproveItems: (ids: string[]) => void;
    onDenyItem: (id: string) => void;
    onDenyItems: (ids: string[]) => void;
    onPendingItem: (id: string) => void;
    onSelectItem: (id: string) => void;
    onViewReceipt: (id: string) => void;
    allCategories: Category[];
}) {
    type ItemSortKey = "status" | "submitter" | "reportId" | "merchant" | "category" | "receipt" | "settleDate" | "exception" | "lastFour" | "amount";

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [sortKey, setSortKey] = useState<ItemSortKey | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    const [columnFilters, setColumnFilters] = useState<Partial<Record<ItemSortKey, string>>>({});
    const [activeFilterColumn, setActiveFilterColumn] = useState<ItemSortKey | null>(null);
    const [activeFilterAnchor, setActiveFilterAnchor] = useState<HTMLElement | null>(null);
    const [filterInput, setFilterInput] = useState("");
    const [searchHistory, setSearchHistory] = useState<ColumnSearchHistory>({});
    const canWrite = useApprovalWriteAccess();

    const items = data?.items ?? [];
    const isItemEditable = useCallback((item: ExpenseItem) => item.isEditable || (subTab === SUB_TAB.Review && item.status === "PENDING"), [subTab]);
    const storageKey = ITEMS_COLUMN_SEARCH_HISTORY_KEY;

    useEffect(() => {
        setSearchHistory(loadColumnSearchHistory(storageKey));
    }, []);

    useEffect(() => {
        saveColumnSearchHistory(storageKey, searchHistory);
    }, [searchHistory]);

    const getItemFilterValue = useCallback((item: ExpenseItem, key: ItemSortKey) => {
        switch (key) {
            case "status":
                return item.status;
            case "submitter":
                return `${item.submitter.firstName} ${item.submitter.lastName} ${item.submitter.email}`;
            case "reportId":
                return item.reportId;
            case "merchant":
                return item.merchant;
            case "category":
                return item.category;
            case "receipt":
                return item.receiptMissing ? "missing" : "available";
            case "settleDate":
                return item.settleDate;
            case "exception":
                return item.exception ?? "";
            case "lastFour":
                return item.lastFour ?? "cash";
            case "amount":
                return `${item.amount} ${fmt$(item.amount)}`;
            default:
                return "";
        }
    }, []);

    const applyColumnFilter = useCallback(() => {
        if (!activeFilterColumn) return;

        const normalizedValue = normalizeColumnFilterValue(filterInput);

        setColumnFilters((previous) => {
            const next = { ...previous };

            if (normalizedValue) {
                next[activeFilterColumn] = normalizedValue;
            } else {
                delete next[activeFilterColumn];
            }

            return next;
        });

        if (normalizedValue) {
            setSearchHistory((previous) => addSearchValueToHistory(previous, activeFilterColumn, normalizedValue));
        }

        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
    }, [activeFilterColumn, filterInput]);

    const clearColumnFilter = useCallback(() => {
        if (!activeFilterColumn) return;

        setColumnFilters((previous) => {
            const next = { ...previous };
            delete next[activeFilterColumn];
            return next;
        });
        setFilterInput("");
        setActiveFilterColumn(null);
        setActiveFilterAnchor(null);
    }, [activeFilterColumn]);

    const removeSavedSearchValue = useCallback(
        (value: string) => {
            if (!activeFilterColumn) return;
            setSearchHistory((previous) => removeSearchValueFromHistory(previous, activeFilterColumn, value));
        },
        [activeFilterColumn],
    );

    const openColumnFilter = useCallback(
        (column: ItemSortKey, columnLabel: string, anchorEl: HTMLElement) => {
            if (activeFilterColumn === column) {
                setActiveFilterColumn(null);
                setActiveFilterAnchor(null);
                return;
            }

            setActiveFilterColumn(column);
            setActiveFilterAnchor(anchorEl);
            setFilterInput(columnFilters[column] ?? "");
            void columnLabel;
        },
        [activeFilterColumn, columnFilters],
    );

    const handleSort = useCallback(
        (key: ItemSortKey) => {
            if (sortKey === key) {
                setSortDirection((prevDir) => (prevDir === "asc" ? "desc" : "asc"));
                return;
            }

            setSortKey(key);
            setSortDirection("asc");
        },
        [sortKey],
    );

    const itemColumnHeader = (key: ItemSortKey, label: string, align: "left" | "right" = "left") => [
        <ColumnHeader
            key={`${key}-header`}
            label={label}
            align={align}
            isFilterable
            isFilterActive={Boolean(columnFilters[key])}
            isFilterOpen={activeFilterColumn === key}
            onFilterPress={(anchorEl) => openColumnFilter(key, label, anchorEl)}
            isSortable
            isSortActive={sortKey === key}
            sortDirection={sortDirection}
            onSort={() => handleSort(key)}
        />,
        <ColumnFilterPopover
            key={`${key}-filter`}
            isOpen={activeFilterColumn === key}
            anchorEl={activeFilterAnchor}
            columnLabel={label}
            searchValue={filterInput}
            savedValues={searchHistory[key] ?? []}
            onSearchChange={setFilterInput}
            onApply={applyColumnFilter}
            onClear={clearColumnFilter}
            onClose={() => {
                setActiveFilterColumn(null);
                setActiveFilterAnchor(null);
            }}
            onSelectValue={(value) => {
                setFilterInput(value);
                setColumnFilters((previous) => ({ ...previous, [key]: value }));
                setSearchHistory((previous) => addSearchValueToHistory(previous, key, value));
                setActiveFilterColumn(null);
                setActiveFilterAnchor(null);
            }}
            onRemoveSavedValue={removeSavedSearchValue}
        />,
    ];

    const sortedItems = useMemo(() => {
        const records = items.filter((item) => {
            return Object.entries(columnFilters).every(([columnKey, filterValue]) => {
                if (!filterValue) return true;

                const itemValue = getItemFilterValue(item, columnKey as ItemSortKey).toLowerCase();
                return itemValue.includes(filterValue.toLowerCase());
            });
        });

        if (!sortKey) return records;

        const direction = sortDirection === "asc" ? 1 : -1;
        const compareString = (left?: string | null, right?: string | null) => (left ?? "").localeCompare(right ?? "") * direction;

        return records.sort((a, b) => {
            switch (sortKey) {
                case "status":
                    return compareString(a.status, b.status);
                case "submitter":
                    return compareString(`${a.submitter.firstName} ${a.submitter.lastName}`, `${b.submitter.firstName} ${b.submitter.lastName}`);
                case "reportId":
                    return compareString(a.reportId, b.reportId);
                case "merchant":
                    return compareString(a.merchant, b.merchant);
                case "category":
                    return compareString(a.category, b.category);
                case "receipt":
                    return (Number(a.receiptMissing) - Number(b.receiptMissing)) * direction;
                case "settleDate":
                    return compareString(a.settleDate, b.settleDate);
                case "exception":
                    return compareString(a.exception, b.exception);
                case "lastFour":
                    return compareString(a.lastFour, b.lastFour);
                case "amount":
                    return (a.amount - b.amount) * direction;
                default:
                    return 0;
            }
        });
    }, [columnFilters, getItemFilterValue, items, sortDirection, sortKey]);

    const editableItemIds = useMemo(() => sortedItems.filter(isItemEditable).map((item) => item.id), [isItemEditable, sortedItems]);

    const hasEditableRows = editableItemIds.length > 0;

    const toggleAll = useCallback(() => {
        if (editableItemIds.length === 0) return;

        const allEditableSelected = editableItemIds.every((id) => selectedIds.has(id));

        if (allEditableSelected) {
            setSelectedIds(new Set());
            return;
        }

        setSelectedIds(new Set(editableItemIds));
    }, [editableItemIds, selectedIds]);

    const toggleOne = useCallback((id: string, isEditable: boolean) => {
        if (!isEditable) return;

        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const handleBulkApprove = () => {
        const ids = [...selectedIds];
        onApproveItems(ids);
        setSelectedIds(new Set());
    };

    const handleBulkDeny = () => {
        const ids = [...selectedIds];
        onDenyItems(ids);
        setSelectedIds(new Set());
    };

    const handleApproveOne = (id: string) => {
        onApproveItem(id);
    };

    const handleDenyOne = (id: string) => {
        onDenyItem(id);
    };

    if (!data) {
        return <DataTableLoading tabName="items" className="mt-6" />;
    }

    const allSelected = editableItemIds.length > 0 && editableItemIds.every((id) => selectedIds.has(id));

    return (
        <div className="w-full overflow-hidden">
            {/* Bulk selection bar */}
            {hasEditableRows && selectedIds.size > 0 && (
                <div className="mb-3 flex items-center justify-between rounded-lg bg-blue-600 px-4 py-2.5 text-white">
                    <span className="text-sm font-medium">{selectedIds.size} items selected</span>
                    <div className="flex gap-2">
                        <button type="button" className="rounded px-3 py-1 text-sm hover:bg-blue-500" onClick={() => setSelectedIds(new Set())}>
                            Clear Selection
                        </button>
                        <button
                            type="button"
                            className="rounded border border-white bg-transparent px-3 py-1 text-sm font-semibold text-white hover:bg-blue-500"
                            onClick={handleBulkDeny}
                            disabled={isMutating || !canWrite}
                        >
                            Deny
                        </button>
                        <button
                            type="button"
                            className="rounded bg-white px-3 py-1 text-sm font-semibold text-blue-700 hover:bg-blue-50"
                            onClick={handleBulkApprove}
                            disabled={isMutating || !canWrite}
                        >
                            Approve
                        </button>
                    </div>
                </div>
            )}

            {/* Mobile card list — shown on screens smaller than xl */}
            <div className="relative w-full divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white xl:hidden">
                <TableLoadingOverlay isLoading={isLoading} label={tableLoadingLabel("items")} />
                {sortedItems.map((item) => (
                    <ItemMobileCard
                        key={item.id}
                        item={item}
                        allCategories={allCategories}
                        isEditable={isItemEditable(item)}
                        isSelected={selectedIds.has(item.id)}
                        isMutating={isMutating}
                        processingItemId={processingItemId}
                        processingAction={processingItemId === item.id ? processingAction : null}
                        currencyCode={data?.currencyCode}
                        onToggle={() => toggleOne(item.id, isItemEditable(item))}
                        onApprove={() => handleApproveOne(item.id)}
                        onDeny={() => handleDenyOne(item.id)}
                        onPending={() => onPendingItem(item.id)}
                        onClickRow={() => onSelectItem(item.id)}
                        onViewReceipt={onViewReceipt}
                    />
                ))}
                {sortedItems.length === 0 && (
                    <p className="flex min-h-40 items-center justify-center px-4 py-8 text-center text-sm text-gray-400">No items found.</p>
                )}
            </div>

            {/* Desktop table — shown on xl and above */}
            <div className="relative hidden rounded-xl border border-gray-200 bg-white xl:block">
                <TableLoadingOverlay isLoading={isLoading} label={tableLoadingLabel("items")} />
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1000px] text-sm">
                        <thead className="relative z-30">
                            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-500">
                                {hasEditableRows && (
                                    <th className="w-8 px-4 py-3">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 accent-blue-600"
                                            checked={allSelected}
                                            onChange={toggleAll}
                                            aria-label="Select all"
                                        />
                                    </th>
                                )}
                                <th className="px-4 py-3">Action</th>
                                {itemColumnHeader("submitter", "Submitter")}
                                {itemColumnHeader("reportId", "Report ID")}
                                {itemColumnHeader("merchant", "Merchant")}
                                {itemColumnHeader("category", "Category")}
                                {itemColumnHeader("receipt", "Receipt")}
                                {itemColumnHeader("settleDate", "Settle Date")}
                                {itemColumnHeader("exception", "Exception")}
                                {itemColumnHeader("lastFour", "Last 4")}
                                {itemColumnHeader("amount", "Amount", "right")}
                            </tr>
                        </thead>
                        <tbody className="relative z-10">
                            {sortedItems.map((item: ExpenseItem) => (
                                <ItemRow
                                    key={item.id}
                                    item={item}
                                    allCategories={allCategories}
                                    isEditable={isItemEditable(item)}
                                    isSelected={selectedIds.has(item.id)}
                                    isMutating={isMutating}
                                    processingItemId={processingItemId}
                                    processingAction={processingItemId === item.id ? processingAction : null}
                                    currencyCode={data?.currencyCode}
                                    onToggle={() => toggleOne(item.id, isItemEditable(item))}
                                    onApprove={() => handleApproveOne(item.id)}
                                    onDeny={() => handleDenyOne(item.id)}
                                    onPending={() => onPendingItem(item.id)}
                                    onClickRow={() => onSelectItem(item.id)}
                                    onViewReceipt={onViewReceipt}
                                />
                            ))}
                            {sortedItems.length === 0 && (
                                <tr>
                                    <td className="h-40 px-4 text-center align-middle text-sm text-gray-400" colSpan={12}>
                                        No items found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ─── Item Mobile Card ─────────────────────────────────────────────────────────

function ItemMobileCard({
    item,
    allCategories,
    isEditable,
    isSelected,
    isMutating,
    processingItemId,
    processingAction,
    currencyCode,
    onToggle,
    onApprove,
    onDeny,
    onPending,
    onClickRow,
    onViewReceipt,
}: {
    item: ExpenseItem;
    allCategories: Category[];
    isEditable: boolean;
    isSelected: boolean;
    isMutating: boolean;
    processingItemId: string | null;
    processingAction: ExpenseAction | null;
    currencyCode: string | undefined;
    onToggle: () => void;
    onApprove: () => void;
    onDeny: () => void;
    onPending: () => void;
    onClickRow: () => void;
    onViewReceipt: (id: string) => void;
}) {
    return (
        <div className={`flex min-w-0 cursor-pointer items-start gap-2 px-3 py-3 hover:bg-blue-50/30 ${isSelected ? "bg-blue-50" : ""}`} onClick={onClickRow}>
            {isEditable && (
                <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 accent-blue-600"
                        checked={isSelected}
                        onChange={onToggle}
                        aria-label={`Select ${item.merchant}`}
                    />
                </div>
            )}
            <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="truncate font-medium text-blue-600">{`${item.submitter.firstName} ${item.submitter.lastName}`.trim()}</p>
                        <p className="text-sm text-gray-700">{resolveItemMerchant(item, allCategories)}</p>
                        <p className="text-xs text-gray-500">{item.category}</p>
                    </div>
                    <div className="shrink-0 text-right">
                        <p className="font-semibold text-gray-900">{formatCurrency(item.amount, item.currencyCode ?? currencyCode)}</p>
                        <p className="text-xs text-gray-500">{fmtDate(item.settleDate)}</p>
                        <p className="text-xs text-gray-400">{item.lastFour ? `····${item.lastFour}` : "Cash"}</p>
                    </div>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs text-gray-500">ID {item.reportId}</span>
                    {item.lostReceiptDeclaration?.declared ? (
                        <span className="text-xs text-blue-600">Lost receipt declared</span>
                    ) : item.receiptMissing ? (
                        <span className="text-xs text-red-500">Missing receipt</span>
                    ) : (
                        <button
                            type="button"
                            className="text-xs text-blue-600 underline"
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewReceipt(item.id);
                            }}
                        >
                            View receipt
                        </button>
                    )}
                    {!item.isPersonal && <ExceptionIndicator violations={item.violations} />}
                    {item.isPersonal && (
                        <span className="inline-flex items-center rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">Personal</span>
                    )}
                </div>
                <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <ExpenseActionButtons
                        itemId={item.id}
                        itemStatus={item.status}
                        isDisabled={isMutating || !isEditable}
                        isProcessing={processingItemId === item.id}
                        processingAction={processingAction}
                        {...getApproveBlockProps(item, allCategories, ReportStatusValue.InReview)}
                        onApprove={onApprove}
                        onDeny={onDeny}
                        onPending={onPending}
                    />
                </div>
            </div>
        </div>
    );
}

function ItemRow({
    item,
    allCategories,
    isEditable,
    isSelected,
    isMutating,
    processingItemId,
    processingAction,
    currencyCode,
    onToggle,
    onApprove,
    onDeny,
    onPending,
    onClickRow,
    onViewReceipt,
}: {
    item: ExpenseItem;
    allCategories: Category[];
    isEditable: boolean;
    isSelected: boolean;
    isMutating: boolean;
    processingItemId: string | null;
    processingAction: ExpenseAction | null;
    currencyCode: string | undefined;
    onToggle: () => void;
    onApprove: () => void;
    onDeny: () => void;
    onPending: () => void;
    onClickRow: () => void;
    onViewReceipt: (id: string) => void;
}) {
    const rowInteractiveClass = "cursor-pointer hover:bg-blue-50/30";

    return (
        <tr className={`border-b border-gray-100 transition ${rowInteractiveClass} ${isSelected ? "bg-blue-50" : ""}`} onClick={onClickRow}>
            {isEditable && (
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 accent-blue-600"
                        checked={isSelected}
                        onChange={onToggle}
                        aria-label={`Select ${item.merchant}`}
                    />
                </td>
            )}
            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                <ExpenseActionButtons
                    itemId={item.id}
                    itemStatus={item.status}
                    isDisabled={isMutating || !isEditable}
                    isProcessing={processingItemId === item.id}
                    processingAction={processingAction}
                    {...getApproveBlockProps(item, allCategories, ReportStatusValue.InReview)}
                    onApprove={onApprove}
                    onDeny={onDeny}
                    onPending={onPending}
                />
            </td>
            <td className="px-4 py-3">
                <p className="font-medium text-blue-600">{`${item.submitter.firstName} ${item.submitter.lastName}`.trim()}</p>
            </td>
            <td className="px-4 py-3 text-gray-700">{item.reportId}</td>
            <td className="px-4 py-3 text-gray-700">
                <span className="flex items-center gap-1.5">
                    {isAiGenerated(item) && (
                        <>
                            <IconAiSparkle className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                            <span className="sr-only">AI-generated</span>
                        </>
                    )}
                    {resolveItemMerchant(item, allCategories)}
                </span>
            </td>
            <td className="px-4 py-3 text-gray-600">{item.category}</td>
            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className="cursor-pointer disabled:cursor-default"
                    disabled={item.receiptMissing}
                    onClick={() => onViewReceipt(item.id)}
                    title={getReceiptIndicator(item).label}
                    aria-label={getReceiptIndicator(item).label}
                >
                    <ReceiptIndicatorIcon expense={item} />
                </button>
            </td>
            <td className="px-4 py-3 text-gray-600">{fmtDate(item.settleDate)}</td>
            <td className="px-4 py-3">
                {item.isPersonal ? (
                    <span className="inline-flex items-center rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">Personal</span>
                ) : getNonReceiptViolations(item.violations).length > 0 ? (
                    <ExceptionIndicator violations={item.violations} />
                ) : (
                    <span className="text-gray-300">—</span>
                )}
            </td>
            <td className="px-4 py-3 text-gray-600">{item.lastFour ?? "Cash"}</td>
            <td className="px-4 py-3 text-right font-medium text-gray-900">{formatCurrency(item.amount, item.currencyCode ?? currencyCode)}</td>
        </tr>
    );
}

function DynamicAutocompleteInput({
    label,
    value,
    onChange,
    isEditable,
    onSearch,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    isEditable: boolean;
    onSearch: (input: string) => Promise<Array<{ description: string; placeId: string | null }>>;
}) {
    const [predictions, setPredictions] = useState<Array<{ description: string; placeId: string | null }>>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const requestSeqRef = useRef(0);
    const skipNextSearchRef = useRef(false);
    const listboxId = useId();

    useEffect(() => {
        setActiveIndex(-1);
    }, [predictions]);

    useEffect(() => {
        if (!isEditable) {
            setIsOpen(false);
            return;
        }

        const query = value.trim();
        if (query.length < 3) {
            setPredictions([]);
            setIsOpen(false);
            setIsLoading(false);
            return;
        }

        if (skipNextSearchRef.current) {
            skipNextSearchRef.current = false;
            setIsLoading(false);
            setIsOpen(false);
            return;
        }

        const timer = window.setTimeout(() => {
            const seq = requestSeqRef.current + 1;
            requestSeqRef.current = seq;
            setIsLoading(true);
            setIsOpen(true);

            void onSearch(query)
                .then((result) => {
                    if (requestSeqRef.current !== seq) return;
                    setPredictions(result);
                    setIsOpen(true);
                })
                .catch(() => {
                    if (requestSeqRef.current !== seq) return;
                    setPredictions([]);
                    setIsOpen(false);
                })
                .finally(() => {
                    if (requestSeqRef.current === seq) {
                        setIsLoading(false);
                    }
                });
        }, 300);

        return () => window.clearTimeout(timer);
    }, [isEditable, onSearch, value]);

    function commitPrediction(idx: number) {
        const prediction = predictions[idx];
        if (!prediction) return;
        skipNextSearchRef.current = true;
        onChange(prediction.description);
        setIsOpen(false);
        setActiveIndex(-1);
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        if (!isOpen || isLoading) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((prev) => Math.min(prev + 1, predictions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((prev) => Math.max(prev - 1, -1));
        } else if (e.key === "Enter") {
            if (activeIndex >= 0) {
                e.preventDefault();
                commitPrediction(activeIndex);
            }
        } else if (e.key === "Escape") {
            e.preventDefault();
            setIsOpen(false);
        }
    }

    const activePredictionId = activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined;

    return (
        <div className="relative" onKeyDown={handleKeyDown}>
            <Input
                label={label}
                value={value}
                onChange={onChange}
                locked={!isEditable}
                inputClassName="pr-10"
                placeholder="Type at least 3 characters"
                aria-autocomplete="list"
                aria-expanded={isEditable && isOpen}
                aria-haspopup="listbox"
                aria-controls={isEditable && isOpen ? listboxId : undefined}
                aria-activedescendant={activePredictionId}
            />
            {isEditable && isOpen && (
                <ul
                    id={listboxId}
                    role="listbox"
                    aria-label={label}
                    className="absolute z-20 mt-1 max-h-44 w-full list-none overflow-y-auto rounded-md border border-gray-200 bg-white p-0 shadow-lg"
                >
                    {isLoading && (
                        <li className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500" aria-live="polite">
                            <SpinnerIcon className="h-3.5 w-3.5 text-blue-500" />
                            <span>Loading...</span>
                        </li>
                    )}
                    {!isLoading && predictions.length === 0 && <li className="px-3 py-2 text-xs text-gray-500">No predictions found</li>}
                    {!isLoading &&
                        predictions.map((prediction, idx) => (
                            <li
                                key={`${prediction.placeId ?? prediction.description}-${idx}`}
                                id={`${listboxId}-opt-${idx}`}
                                role="option"
                                aria-selected={activeIndex === idx}
                                className={`cursor-pointer px-3 py-2 text-sm text-gray-700 ${activeIndex === idx ? "bg-gray-100" : "hover:bg-gray-50"}`}
                                onMouseDown={(e) => {
                                    // mousedown fires before blur so the dropdown stays open long enough for the click
                                    e.preventDefault();
                                    commitPrediction(idx);
                                }}
                            >
                                {prediction.description}
                            </li>
                        ))}
                </ul>
            )}
        </div>
    );
}

function DynamicTextInput({ label, value, onCommit, isEditable }: { label: string; value: string; onCommit: (value: string) => void; isEditable: boolean }) {
    const [draftValue, setDraftValue] = useState(value);

    useEffect(() => {
        setDraftValue(value);
    }, [value]);

    if (!isEditable) {
        return <Input label={label} value={value} locked inputClassName="pr-10" />;
    }

    return (
        <Input
            label={label}
            value={draftValue}
            onChange={setDraftValue}
            onBlur={() => {
                if (draftValue !== value) onCommit(draftValue);
            }}
            inputClassName="pr-10"
            placeholder={value.length === 0 ? "Type to edit" : undefined}
        />
    );
}

function ParticipantsModal({
    isOpen,
    initialNames,
    onClose,
    onSave,
}: {
    isOpen: boolean;
    initialNames: string[];
    onClose: () => void;
    onSave: (names: string[]) => void;
}) {
    const titleId = useId();
    const [participantInput, setParticipantInput] = useState("");
    const [names, setNames] = useState<string[]>(initialNames);

    useEffect(() => {
        if (isOpen) {
            setNames(initialNames);
            setParticipantInput("");
        }
    }, [isOpen, initialNames]);

    if (!isOpen) return null;

    return (
        <ModalOverlay
            isOpen
            isDismissable
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30 p-4"
        >
            <Modal className="w-full max-w-2xl">
                <Dialog aria-labelledby={titleId} className="outline-none">
                    <div className="space-y-4 rounded-xl bg-white p-6">
                        <div className="flex items-center justify-between">
                            <h3 id={titleId} className="text-2xl font-medium text-gray-900">
                                Add Participants
                            </h3>
                            <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose} aria-label="Close participants modal">
                                <IconX />
                            </button>
                        </div>

                        <div className="flex items-end gap-3">
                            <div className="flex-1">
                                <Input
                                    label="Participant"
                                    value={participantInput}
                                    onChange={setParticipantInput}
                                    placeholder="Type participant name"
                                    inputClassName="pr-10"
                                />
                            </div>
                            <button
                                type="button"
                                className="h-10 rounded-full bg-blue-600 px-6 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                                disabled={participantInput.trim().length === 0}
                                onClick={() => {
                                    const next = participantInput.trim();
                                    if (!next) return;
                                    setNames((prev) => [...prev, next]);
                                    setParticipantInput("");
                                }}
                            >
                                Add
                            </button>
                        </div>

                        <div className="max-h-56 overflow-y-auto rounded-md border border-gray-200 px-2 py-1">
                            {names.length === 0 ? (
                                <p className="px-2 py-3 text-sm text-gray-500">No participants added</p>
                            ) : (
                                names.map((name, idx) => (
                                    <div key={`${name}-${idx}`} className="flex items-center justify-between px-2 py-2">
                                        <span className="text-sm text-gray-800">{name}</span>
                                        <button
                                            type="button"
                                            className="text-xs font-medium text-red-600 hover:text-red-700"
                                            onClick={() => setNames((prev) => prev.filter((_, i) => i !== idx))}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="border-t border-gray-200 pt-4 text-center">
                            <button
                                type="button"
                                className="h-10 rounded-full bg-blue-600 px-8 text-sm font-semibold text-white hover:bg-blue-700"
                                onClick={() => onSave(names)}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}

// ─── Expense Detail Panel ─────────────────────────────────────────────────────

// A regular expense comment as returned by getExpenseComments.
interface ExpenseCommentItem {
    commentId?: string;
    text: string;
    authorFullName?: string;
    authorId?: string;
    createdAt: string;
}

// A policy-violation comment, flattened with its policy name for display.
interface ViolationCommentItem {
    id: string;
    policyName: string | null;
    text: string;
    authorFullName: string;
    createdAt: string;
}

function ExpenseDetailPanel({
    item,
    allCategories,
    isLoadingReceipt,
    isMutating,
    processingItemId,
    processingAction,
    showReviewActions,
    onApprove,
    onDeny,
    onPending,
    getLocationPredictions,
    onUpdateExpenseCategory,
    getExpenseComments,
    getPolicyViolationComments,
    createExpenseComment,
    getExpenseHistory,
    onClose,
}: {
    item: FullExpenseDetail | null;
    allCategories: Category[];
    isLoadingReceipt: boolean;
    isMutating: boolean;
    processingItemId: string | null;
    processingAction: ExpenseAction | null;
    showReviewActions: boolean;
    onApprove: (id: string) => void;
    onDeny: (id: string) => void;
    onPending: (id: string) => void;
    getLocationPredictions: (input: string) => Promise<Array<{ description: string; placeId: string | null }>>;
    onUpdateExpenseCategory: (
        itemId: string,
        selectedCategory: Category,
        fieldOverrides?: Record<string, unknown>,
    ) => Promise<{ success: boolean; message?: string | null; violations?: PolicyViolation[] }>;
    getExpenseComments?: (expenseId: string) => Promise<any>;
    getPolicyViolationComments?: (violationId: string) => Promise<any>;
    createExpenseComment?: (expenseId: string, text: string) => Promise<any>;
    getExpenseHistory?: (expenseId: string) => Promise<any>;
    onClose: () => void;
}) {
    const [activeDetailTab, setActiveDetailTab] = useState<DetailTab>(DETAIL_TAB.Details);
    const [pendingTab, setPendingTab] = useState<DetailTab | null>(null);
    const [fileViewerOpen, setFileViewerOpen] = useState(false);
    const [fileViewerIndex, setFileViewerIndex] = useState(0);
    const [cardIndex, setCardIndex] = useState(0);
    const [footerActionLoading, setFooterActionLoading] = useState<"deny" | "approve" | "cancel" | "save" | null>(null);
    const [updateErrorMessage, setUpdateErrorMessage] = useState<string | null>(null);
    const [violationPopup, setViolationPopup] = useState<PolicyViolationModalState | null>(null);

    // Category select — initialise from ULID first, then legacy/subCategoryId for older expenses
    const resolvedCategorySelectKey = useMemo(() => {
        if (!item) return null;
        if (item.category) {
            const matchedByUlid = allCategories.find((c) => c.categoryId === item.category);
            if (matchedByUlid) return matchedByUlid.categoryId;

            const numericCategory = Number(item.category);
            const isNumericLegacyString = Number.isFinite(numericCategory) && !Number.isNaN(numericCategory);
            // Legacy expenses keep a label in `category` and the legacy id in `subCategoryId`.
            const matchedBySubCategoryLegacy = findItemCategory({ category: "", subCategoryId: item.subCategoryId }, allCategories);
            if (!isNumericLegacyString) {
                if (matchedBySubCategoryLegacy) return matchedBySubCategoryLegacy.categoryId;
                // Expense stores a ULID that is not in the active list (e.g. deactivated category).
                return item.category;
            }

            const matchedByNumericLegacy = allCategories.find((c) => c.legacyId === numericCategory);
            if (matchedByNumericLegacy) return matchedByNumericLegacy.categoryId;
        }
        const matchedByLegacy = findItemCategory({ category: "", subCategoryId: item.subCategoryId }, allCategories);
        if (matchedByLegacy) return matchedByLegacy.categoryId;
        return null;
    }, [item, allCategories]);

    const [selectedCategorySelectKey, setSelectedCategorySelectKey] = useState<string | null>(resolvedCategorySelectKey);
    const hasCategoryChanges = (selectedCategorySelectKey ?? "") !== (resolvedCategorySelectKey ?? "");

    // Reset when a new expense is opened
    useEffect(() => {
        setSelectedCategorySelectKey(resolvedCategorySelectKey);
    }, [resolvedCategorySelectKey]);

    useEffect(() => {
        setUpdateErrorMessage(null);
        setViolationPopup(null);
    }, [item?.id]);

    // Local edits to category-specific extra fields (stateName → value).
    // Reset whenever a different expense is opened.
    const [extraFieldValues, setExtraFieldValues] = useState<Record<string, unknown>>({});
    const [participantsModalState, setParticipantsModalState] = useState<{ key: string; names: string[] } | null>(null);
    useEffect(() => {
        setExtraFieldValues({});
        setParticipantsModalState(null);
    }, [item?.id]);

    const selectedCategoryOption = useMemo(() => {
        if (!selectedCategorySelectKey) return null;
        return allCategories.find((c) => c.categoryId === selectedCategorySelectKey) ?? null;
    }, [allCategories, selectedCategorySelectKey]);

    // Completed reports show the category data frozen at approval time; live category data is only a fallback.
    const isReportApproved = item?.reportState === ReportStatusValue.Approved;
    const frozenCategory = isReportApproved ? (item?.frozenCategory ?? null) : null;

    const selectedCategoryLegacyId = useMemo(() => {
        return frozenCategory?.legacyId ?? selectedCategoryOption?.legacyId ?? item?.subCategoryId ?? null;
    }, [frozenCategory?.legacyId, selectedCategoryOption, item?.subCategoryId]);

    const isMileageExpense = useMemo(() => {
        if (isMileage(selectedCategoryLegacyId)) return true;
        return item ? isMileageCategoryId(item.category, allCategories) : false;
    }, [selectedCategoryLegacyId, item, allCategories]);

    // Account code comes from the frozen snapshot when one exists (blank stays blank), otherwise from the selected category.
    const selectedCategoryAccountCode = useMemo(() => {
        return frozenCategory ? (frozenCategory.accountCode ?? "") : (selectedCategoryOption?.accountCode ?? "");
    }, [frozenCategory, selectedCategoryOption]);

    // Flat, alphabetized category options for the Select dropdown (APP-285: no group headers).
    // API already sorts by categoryName asc; the client sort keeps the ordering contract local.
    const categoryOptions = useMemo(
        () =>
            allCategories
                .filter((cat) => String(cat.status).toLowerCase() === CATEGORY_STATUS.Active && !isMileage(cat.legacyId))
                .map((category) => ({
                    key: category.categoryId,
                    label: category.categoryName,
                }))
                .sort((left, right) => left.label.localeCompare(right.label, undefined, { sensitivity: "base" })),
        [allCategories],
    );

    // Current value shown when the saved category is no longer selectable (deactivated/deleted).
    const savedCategoryFallbackLabel = useMemo(
        () =>
            selectedCategorySelectKey
                ? resolveCategoryName(selectedCategorySelectKey, allCategories, item?.reportState ?? undefined, item?.frozenCategory?.categoryName)
                : undefined,
        [allCategories, item?.reportState, item?.frozenCategory?.categoryName, selectedCategorySelectKey],
    );

    // Completed reports always show the category name frozen at approval time, even if the category was later renamed.
    const frozenCategoryLabel = frozenCategory?.categoryName ?? undefined;
    const isSelectedCategoryBlocked = !isReportApproved && !!getCategoryBlockStatus(selectedCategorySelectKey, allCategories);

    // Lost Receipt Protocol: purely reflect the saved declaration — no client-side
    // calculation/escalation logic here, per spec (matches WebV5's approver view).
    const lostReceiptDeclaration: LostReceiptDeclaration | null = item?.lostReceiptDeclaration ?? null;

    const receiptFiles = useMemo<FileViewerFile[]>(
        () => (item?.receipts ?? []).flatMap((r) => (r.s3URL ? [{ url: r.s3URL, mimeType: r.fileType ?? "application/octet-stream", id: r.receiptId }] : [])),
        [item?.receipts],
    );

    // Reset card index whenever the expense changes
    useEffect(() => {
        setCardIndex(0);
    }, [item?.id]);

    // Keep footer spinner state in sync with the item/action context.
    useEffect(() => {
        setFooterActionLoading(null);
    }, [item?.id, hasCategoryChanges]);

    useEffect(() => {
        if (!isMutating && (footerActionLoading === "deny" || footerActionLoading === "approve")) {
            setFooterActionLoading(null);
        }
    }, [isMutating, footerActionLoading]);

    const isLoading = isLoadingReceipt || !item;

    const submittedAtLabel = formatDateWithToken(item?.submittedAt, "M/d/yyyy, hh:mm a", "—");

    const businessBadgeClass = item?.isPersonal ? "bg-red-500 text-white" : "bg-red-500 text-white";

    const isApproved = item?.status === ItemStatusValue.Approved;
    const isDenied = item?.status === ItemStatusValue.Rejected;

    const [expenseHistory, setExpenseHistory] = useState<Array<any>>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);

    const [expenseComments, setExpenseComments] = useState<ExpenseCommentItem[]>([]);
    const [violationComments, setViolationComments] = useState<ViolationCommentItem[]>([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [commentsLoaded, setCommentsLoaded] = useState(false);
    const [commentsError, setCommentsError] = useState<string | null>(null);
    const [newCommentText, setNewCommentText] = useState("");
    const [submittingComment, setSubmittingComment] = useState(false);
    const canWrite = useApprovalWriteAccess();
    const composerDisabled = item?.reportState === ReportStatusValue.Approved;
    // Styled as disabled but not prop-disabled, so the explanatory tooltip still shows on hover.
    const isViolationBlocked = !isReportApproved && !!item && hasBlockingViolation(item);
    const isApproveActionBlocked = isSelectedCategoryBlocked || isViolationBlocked;
    // The category block leads and is never combined with the violation tooltip.
    const approveBlockedTooltip = isSelectedCategoryBlocked ? CATEGORY_ITEM_APPROVE_TOOLTIP : ITEM_VIOLATION_APPROVE_TOOLTIP;
    const isFooterApproveBlocked =
        isApproveActionBlocked && !!item && !isMutating && footerActionLoading === null && canWrite && item.status !== ItemStatusValue.Approved;

    useEffect(() => {
        setExpenseComments([]);
        setViolationComments([]);
        setLoadingComments(false);
        setCommentsLoaded(false);
        setCommentsError(null);
        setNewCommentText("");
    }, [item?.id]);

    useEffect(() => {
        setExpenseHistory([]);
        setLoadingHistory(false);
        setHistoryLoaded(false);
        setHistoryError(null);
    }, [item?.id]);

    const loadHistory = useCallback(() => {
        if (!item || !getExpenseHistory) return;
        setLoadingHistory(true);
        setHistoryError(null);
        getExpenseHistory(item.id)
            .then((res) => {
                setExpenseHistory(Array.isArray(res) ? res : (res?.items ?? []));
                setHistoryLoaded(true);
            })
            .catch((error: unknown) => {
                setHistoryError(error instanceof Error ? error.message : "Failed to load history");
            })
            .finally(() => setLoadingHistory(false));
    }, [item, getExpenseHistory]);

    useEffect(() => {
        if (activeDetailTab === DETAIL_TAB.History && item && !historyLoaded && !loadingHistory && !historyError && getExpenseHistory) {
            loadHistory();
        }
    }, [activeDetailTab, item, historyLoaded, loadingHistory, historyError, getExpenseHistory, loadHistory]);

    const loadCommunications = useCallback(async () => {
        if (!item) return;
        const regularPromise = getExpenseComments ? getExpenseComments(item.id) : Promise.resolve([]);
        const violationsNeedingExplanation = (item.violations ?? []).filter(
            (v: any) => Array.isArray(v.actions) && v.actions.some((a: any) => a.type === VIOLATION_ACTION_REQUIRE_EXPLANATION),
        );
        const violationResults = getPolicyViolationComments
            ? await Promise.all(
                  violationsNeedingExplanation.map((v: any) =>
                      getPolicyViolationComments(v.violationId)
                          .then((res: any) => ({ violation: v, items: Array.isArray(res) ? res : (res?.items ?? []) }))
                          .catch(() => ({ violation: v, items: [] as any[] })),
                  ),
              )
            : [];
        const regularRes = await regularPromise;
        const regular = (Array.isArray(regularRes) ? regularRes : (regularRes?.items ?? [])) as any[];
        const merged = violationResults.flatMap(({ violation, items }) =>
            items.map((c: any) => ({
                id: `pv-${violation.violationId}-${c.commentId ?? ""}`,
                policyName: violation.policyName || null,
                text: c.text,
                authorFullName: c.authorFullName ?? c.authorId,
                createdAt: c.createdAt,
            })),
        );
        setExpenseComments(regular);
        setViolationComments(merged);
    }, [item, getExpenseComments, getPolicyViolationComments]);

    useEffect(() => {
        let mounted = true;
        if (activeDetailTab === DETAIL_TAB.Communications && item && !commentsLoaded) {
            setLoadingComments(true);
            setCommentsError(null);
            void loadCommunications()
                .then(() => {
                    if (mounted) setCommentsLoaded(true);
                })
                .catch((err: unknown) => {
                    if (!mounted) return;
                    const msg = err instanceof Error ? err.message : "Failed to load comments";
                    setCommentsError(msg);
                })
                .finally(() => mounted && setLoadingComments(false));
        }
        return () => {
            mounted = false;
        };
    }, [activeDetailTab, item, commentsLoaded, loadCommunications]);

    const handleCancelCategoryChanges = () => {
        setFooterActionLoading("cancel");
        setSelectedCategorySelectKey(resolvedCategorySelectKey);
        setFooterActionLoading(null);
    };

    // Informational popup for active non-receipt violations returned by a save; submitted values win over the pre-save item.
    const openViolationPopup = (violations: PolicyViolation[] | undefined, fieldOverrides: Record<string, unknown>) => {
        if (!item) return;
        const popupViolations = getPopupViolations(violations);
        if (popupViolations.length === 0) return;
        const { merchant, amount, chargeDate } = fieldOverrides;
        setViolationPopup({
            tone: getViolationPopupTone(popupViolations),
            policyName: popupViolations.find(violationRequiresExplanation)?.policyName ?? popupViolations[0].policyName,
            merchant: typeof merchant === "string" ? merchant : item.merchant,
            amount: formatCurrency(amount != null ? Number(amount) : item.amount, item.currencyCode, ""),
            date: formatDateWithToken(typeof chargeDate === "string" ? chargeDate : item.chargeDate, "MMM d, yyyy", "—"),
        });
    };

    const handleSaveCategoryChanges = async () => {
        if (!item) return;
        if (!selectedCategoryOption) {
            setUpdateErrorMessage("Unable to resolve category IDs for this expense.");
            return;
        }

        const selectedExtraFields = getCategoryExtraFields(selectedCategoryLegacyId);
        const fieldOverrides: Record<string, unknown> = {};

        for (const field of selectedExtraFields) {
            if (!field.apiKey) continue;
            const key = field.apiKey;
            if (!Object.prototype.hasOwnProperty.call(extraFieldValues, key)) continue;
            fieldOverrides[key] = extraFieldValues[key];

            if (field.inputConfig.inputType === expenseInputTypes.MONEY && field.inputConfig.currencyApiKey) {
                const currencyKey = field.inputConfig.currencyApiKey;
                if (Object.prototype.hasOwnProperty.call(extraFieldValues, currencyKey)) {
                    fieldOverrides[currencyKey] = extraFieldValues[currencyKey];
                }
            }
        }

        if (Object.prototype.hasOwnProperty.call(extraFieldValues, "participants")) {
            fieldOverrides.participants = extraFieldValues.participants;
        }

        setFooterActionLoading("save");
        try {
            const result = await onUpdateExpenseCategory(item.id, selectedCategoryOption, fieldOverrides);
            if (!result.success) {
                setUpdateErrorMessage(result.message ?? "Unable to update the expense category.");
            } else {
                openViolationPopup(result.violations, fieldOverrides);
            }
        } finally {
            setFooterActionLoading(null);
        }
    };

    const handleFooterDeny = () => {
        if (!item) return;
        setFooterActionLoading("deny");
        onDeny(item.id);
    };

    const handleFooterApprove = async () => {
        if (!item) return;
        setFooterActionLoading("approve");

        // If the expense's category is stored as a numeric legacyId string (e.g. "23"),
        // it was resolved to a real category via legacyId matching. Auto-save the
        // resolved ULID categoryId before approving so the backend receives a proper categoryId.
        if (selectedCategoryOption && !hasCategoryChanges) {
            const numericCategory = Number(item.category);
            const categoryIsNumericLegacyId =
                Number.isFinite(numericCategory) && !Number.isNaN(numericCategory) && selectedCategoryOption.categoryId !== item.category;
            if (categoryIsNumericLegacyId) {
                const result = await onUpdateExpenseCategory(item.id, selectedCategoryOption, {});
                if (!result.success) {
                    setUpdateErrorMessage(result.message ?? "Failed to update expense category before approval.");
                    setFooterActionLoading(null);
                    return;
                }
                // The save re-evaluates policies, so approval must use its violations, not the pre-save ones.
                if (result.violations && hasBlockingViolation({ ...item, violations: result.violations })) {
                    openViolationPopup(result.violations, {});
                    setFooterActionLoading(null);
                    return;
                }
            }
        }

        onApprove(item.id);
    };

    return (
        <>
            {/* Receipt viewer */}
            <FileViewer files={receiptFiles} isOpen={fileViewerOpen} onOpenChange={setFileViewerOpen} initialIndex={fileViewerIndex} />

            {/* Backdrop */}
            <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose} aria-hidden="true" />

            {/* Panel */}
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Expense Details"
                className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[700px] flex-col overflow-hidden overflow-x-hidden bg-white shadow-2xl"
            >
                {/* Header — single row: title + actions + close */}
                <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-3">
                    {/* Title + action buttons */}
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                        <h2 className="shrink-0 text-xl font-medium leading-tight text-gray-900 sm:text-2xl sm:leading-none">Expense Details</h2>
                        {showReviewActions && item ? (
                            <ExpenseActionButtons
                                itemId={item.id}
                                itemStatus={item.status}
                                isDisabled={isMutating || !item}
                                isProcessing={processingItemId === item.id}
                                processingAction={processingItemId === item.id ? processingAction : null}
                                hasUnsavedChanges={hasCategoryChanges}
                                isApproveBlocked={isApproveActionBlocked}
                                blockedTooltip={approveBlockedTooltip}
                                onApprove={onApprove}
                                onDeny={onDeny}
                                onPending={onPending}
                            />
                        ) : (
                            <StatusBadge status={item?.status ?? "PENDING"} />
                        )}
                    </div>
                    {/* Close button */}
                    <button
                        type="button"
                        aria-label="Close"
                        className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                        onClick={onClose}
                    >
                        <IconX />
                    </button>
                </div>

                {/* Sticky non-scrolling area: amount + submitter + tabs */}
                <div className="shrink-0 px-6 pt-4">
                    {item && (
                        <>
                            {/* Amount + submitter */}
                            <div className="mb-4">
                                <div className="flex flex-wrap items-baseline gap-2">
                                    <p className="text-[40px] leading-tight font-semibold text-gray-900">
                                        {formatCurrency(item.amount, item.currencyCode, "")}{" "}
                                        <span className="text-lg font-normal text-gray-500">{item.currencyCode ?? "USD"}</span>
                                    </p>
                                    <span className={`-translate-y-2 rounded px-2.5 py-1 text-[10px] font-semibold ${businessBadgeClass}`}>
                                        {item.isPersonal ? "Personal" : "Business"}
                                    </span>
                                </div>
                                <p className="mt-1 flex flex-wrap items-center gap-x-1 text-xs text-gray-500">
                                    <span>Submitted by</span> <span className="font-medium text-blue-600">{item.submittedBy}</span>{" "}
                                    <span>on {submittedAtLabel}</span> <span>Report #:</span>{" "}
                                    <span className="font-medium text-gray-600">{item.reportNumber}</span>
                                </p>
                            </div>

                            {/* Detail tabs */}
                            <div className="flex flex-wrap gap-5">
                                {([DETAIL_TAB.Details, DETAIL_TAB.History, DETAIL_TAB.Communications] as const).map((tab) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        className={`pb-2 text-sm capitalize transition ${
                                            activeDetailTab === tab
                                                ? "border-b-2 border-blue-600 font-semibold text-blue-600"
                                                : "text-gray-500 hover:text-gray-800"
                                        }`}
                                        onClick={() => {
                                            if (hasCategoryChanges && activeDetailTab === DETAIL_TAB.Details && tab !== DETAIL_TAB.Details) {
                                                setPendingTab(tab);
                                            } else {
                                                setActiveDetailTab(tab);
                                            }
                                        }}
                                    >
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Scrollable content — only tab body scrolls */}
                <div className="relative flex-1 overflow-x-hidden overflow-y-auto px-6 py-4">
                    {/* Loading overlay */}
                    {isLoading && <LoadingState variant={LOADING_STATE_VARIANT.Overlay} label="Loading expense" message="Loading expense details…" />}
                    {item && (
                        <>
                            {activeDetailTab === DETAIL_TAB.Details && (
                                <div className="flex flex-col gap-4 pb-4">
                                    {isAiGenerated(item) && <AiDetailStrip />}
                                    {!isMileageExpense && <Input label="Merchant" value={item.merchant} locked inputClassName="pr-10" />}

                                    {/* Category/Mileage Calculation + Account Code */}
                                    <div className="grid grid-cols-2 gap-3">
                                        {isMileageExpense ? (
                                            <Select label="Mileage Calculation" selectedKey="address" isDisabled locked>
                                                <Select.Item id="address">Address</Select.Item>
                                            </Select>
                                        ) : (
                                            <div>
                                                <DropdownSelector
                                                    label="Category"
                                                    labelAdornment={isAiGenerated(item) ? <IconAiSparkle className="h-3.5 w-3.5 text-blue-500" /> : undefined}
                                                    selectedKey={selectedCategorySelectKey}
                                                    data={categoryOptions}
                                                    currentValueFallbackLabel={savedCategoryFallbackLabel}
                                                    currentValueLabelOverride={frozenCategoryLabel}
                                                    hasError={isSelectedCategoryBlocked}
                                                    isEditable={showReviewActions}
                                                    onSelectionChange={(key) => setSelectedCategorySelectKey(key)}
                                                    enableSearch={true}
                                                    searchPlaceholder="Search category..."
                                                    emptyText="No categories found"
                                                />
                                                {isAiGenerated(item) && <AiFieldHint text="Suggested by AI" />}
                                            </div>
                                        )}
                                        <Input label="Account Code" value={selectedCategoryAccountCode} copyable inputClassName="pr-10" />
                                    </div>

                                    {/* Charge amount + Card currency amount */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <Input
                                            label="Charge amount"
                                            value={`${formatCurrency(item.chargeAmount, item.currencyCode)} (${item.currencyCode ?? "USD"})`}
                                            locked
                                            inputClassName="pr-10"
                                        />
                                        <Input
                                            label="Card currency amount"
                                            value={`${formatCurrency(item.cardCurrencyAmount, item.cardCurrencyCode)} (${item.cardCurrencyCode ?? "USD"})`}
                                            locked
                                            inputClassName="pr-10"
                                        />
                                    </div>

                                    {!isMileageExpense && (
                                        <Input
                                            label="Payment Method"
                                            value={
                                                item.paymentMethod
                                                    ? item.paymentMethod.method === PAYMENT_METHOD.CreditCard
                                                        ? `${item.paymentMethod.display.description ?? item.paymentMethod.method} ····${item.paymentMethod.display.last4 ?? ""}`
                                                        : item.paymentMethod.method
                                                    : "—"
                                            }
                                            locked
                                            inputClassName="pr-10"
                                        />
                                    )}

                                    {/* Description */}
                                    <div>
                                        {isAiGenerated(item) && (
                                            <span className="mb-1 flex items-center gap-1 text-sm font-medium text-gray-700">
                                                Describe <IconAiSparkle className="h-3.5 w-3.5 text-blue-500" />
                                            </span>
                                        )}
                                        <TextArea
                                            label={isAiGenerated(item) ? "" : "Description"}
                                            aria-label="Description"
                                            value={item.description ?? "—"}
                                            locked
                                            rows={4}
                                            textAreaClassName="resize-none pr-10"
                                        />
                                        {isAiGenerated(item) && <AiFieldHint text="Written by AI" />}
                                    </div>

                                    {isMileageExpense && (
                                        <>
                                            <div className="grid grid-cols-2 gap-3">
                                                <Input label="From" value={item.milageLocationFrom ?? "—"} locked inputClassName="pr-10" />
                                                <Input label="To" value={item.milageLocationTo ?? "—"} locked inputClassName="pr-10" />
                                            </div>
                                            <div className="grid grid-cols-3 gap-3">
                                                <Input label="Distance" value={item.distance ?? "—"} locked inputClassName="pr-10" />
                                                <Input
                                                    label="Rate"
                                                    value={
                                                        item.rate != null && item.rate !== "" && Number.isFinite(Number(item.rate))
                                                            ? `${formatCurrency(Number(item.rate), "USD")} (USD)`
                                                            : "—"
                                                    }
                                                    locked
                                                    inputClassName="pr-10"
                                                />
                                                <Input label="Unit" value={item.distanceRate ?? "—"} locked inputClassName="pr-10" />
                                            </div>
                                        </>
                                    )}

                                    {/* Category-specific extra fields — derived from selected category */}
                                    {!isMileageExpense &&
                                        buildExtraFieldRows(getCategoryExtraFields(selectedCategoryLegacyId)).map((row, rowIdx) => {
                                            if (row.length === 1) {
                                                const field = row[0];
                                                const itemValue = getExpenseDetailFieldValue(item, field);
                                                const isEmpty = itemValue === "—";
                                                const isEditable = showReviewActions && hasCategoryChanges && isEmpty;
                                                const fieldStoreKey = field.apiKey ?? field.stateName;
                                                const storedValue = extraFieldValues[fieldStoreKey];
                                                const fieldValue = typeof storedValue === "string" ? storedValue : isEmpty ? "" : itemValue;

                                                if (field.inputConfig.format === "DateTime") {
                                                    const sourceFromItem = field.apiKey ? (item as unknown as Record<string, unknown>)[field.apiKey] : null;
                                                    const dateString =
                                                        typeof storedValue === "string"
                                                            ? storedValue
                                                            : typeof sourceFromItem === "string"
                                                              ? sourceFromItem
                                                              : "";
                                                    return (
                                                        <div key={field.stateName} className="flex flex-col gap-1">
                                                            <label className="text-sm font-medium text-gray-700">{field.placeholder}</label>
                                                            <DatePicker
                                                                aria-label={field.placeholder}
                                                                value={toDateValue(dateString)}
                                                                locked={!isEditable}
                                                                className={DATE_PICKER_FIELD_CLASS}
                                                                onChange={
                                                                    isEditable
                                                                        ? (nextDate) =>
                                                                              setExtraFieldValues((prev) => ({
                                                                                  ...prev,
                                                                                  [fieldStoreKey]: nextDate ? nextDate.toString() : "",
                                                                              }))
                                                                        : undefined
                                                                }
                                                            />
                                                        </div>
                                                    );
                                                }

                                                if (field.inputConfig.inputType === expenseInputTypes.AUTO_COMPLETE) {
                                                    return (
                                                        <DynamicAutocompleteInput
                                                            key={field.stateName}
                                                            label={field.placeholder}
                                                            value={fieldValue}
                                                            isEditable={isEditable}
                                                            onSearch={getLocationPredictions}
                                                            onChange={(val) => setExtraFieldValues((prev) => ({ ...prev, [fieldStoreKey]: val }))}
                                                        />
                                                    );
                                                }

                                                if (field.inputConfig.inputType === expenseInputTypes.PARTICIPANT) {
                                                    const sourceFromItem = field.apiKey ? (item as unknown as Record<string, unknown>)[field.apiKey] : null;
                                                    const participants = Array.isArray(storedValue)
                                                        ? storedValue.map(String)
                                                        : Array.isArray(sourceFromItem)
                                                          ? sourceFromItem.map(String)
                                                          : [];
                                                    const participantsLabel = participants.join(", ");
                                                    return (
                                                        <div key={field.stateName} className={isEditable ? "cursor-pointer" : ""}>
                                                            <div
                                                                onClick={() => {
                                                                    if (!isEditable) return;
                                                                    setParticipantsModalState({ key: fieldStoreKey, names: participants });
                                                                }}
                                                            >
                                                                <Input
                                                                    label={field.placeholder}
                                                                    value={participantsLabel}
                                                                    locked={!isEditable}
                                                                    inputClassName="pr-10"
                                                                    placeholder={isEditable ? "Click to add participants" : undefined}
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                if (field.inputConfig.inputType === expenseInputTypes.MONEY) {
                                                    const currencyStoreKey = field.inputConfig.currencyApiKey ?? "currencyCode";
                                                    const rawCurrency = extraFieldValues[currencyStoreKey];
                                                    const selectedCurrency =
                                                        typeof rawCurrency === "string" && rawCurrency.length > 0 ? rawCurrency : (item.currencyCode ?? "USD");
                                                    const moneyValue = typeof storedValue === "string" ? storedValue : "";
                                                    const canEditMoney = showReviewActions && hasCategoryChanges && isEmpty;

                                                    return (
                                                        <div key={field.stateName} className="grid grid-cols-[2fr_1fr] gap-2">
                                                            <Input
                                                                label={field.placeholder}
                                                                value={moneyValue}
                                                                placeholder="0.00"
                                                                locked={!canEditMoney}
                                                                inputClassName="pr-10"
                                                                onChange={
                                                                    canEditMoney
                                                                        ? (val) => {
                                                                              if (/^\d*(\.\d{0,2})?$/.test(val)) {
                                                                                  setExtraFieldValues((prev) => ({ ...prev, [fieldStoreKey]: val }));
                                                                              }
                                                                          }
                                                                        : undefined
                                                                }
                                                            />
                                                            <Input label="Currency" value={selectedCurrency} locked />
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <DynamicTextInput
                                                        key={field.stateName}
                                                        label={field.placeholder}
                                                        value={fieldValue}
                                                        isEditable={isEditable}
                                                        onCommit={(val) => setExtraFieldValues((prev) => ({ ...prev, [fieldStoreKey]: val }))}
                                                    />
                                                );
                                            }
                                            return (
                                                <div key={rowIdx} className="grid grid-cols-2 gap-3">
                                                    {row.map((field) => {
                                                        const itemValue = getExpenseDetailFieldValue(item, field);
                                                        const isEmpty = itemValue === "—";
                                                        const isEditable = showReviewActions && hasCategoryChanges && isEmpty;
                                                        const fieldStoreKey = field.apiKey ?? field.stateName;
                                                        const storedValue = extraFieldValues[fieldStoreKey];
                                                        const fieldValue = typeof storedValue === "string" ? storedValue : isEmpty ? "" : itemValue;

                                                        if (field.inputConfig.format === "DateTime") {
                                                            const sourceFromItem = field.apiKey
                                                                ? (item as unknown as Record<string, unknown>)[field.apiKey]
                                                                : null;
                                                            const dateString =
                                                                typeof storedValue === "string"
                                                                    ? storedValue
                                                                    : typeof sourceFromItem === "string"
                                                                      ? sourceFromItem
                                                                      : "";
                                                            return (
                                                                <div key={field.stateName} className="flex flex-col gap-1">
                                                                    <label className="text-sm font-medium text-gray-700">{field.placeholder}</label>
                                                                    <DatePicker
                                                                        aria-label={field.placeholder}
                                                                        value={toDateValue(dateString)}
                                                                        locked={!isEditable}
                                                                        className={DATE_PICKER_FIELD_CLASS}
                                                                        onChange={
                                                                            isEditable
                                                                                ? (nextDate) =>
                                                                                      setExtraFieldValues((prev) => ({
                                                                                          ...prev,
                                                                                          [fieldStoreKey]: nextDate ? nextDate.toString() : "",
                                                                                      }))
                                                                                : undefined
                                                                        }
                                                                    />
                                                                </div>
                                                            );
                                                        }

                                                        if (field.inputConfig.inputType === expenseInputTypes.AUTO_COMPLETE) {
                                                            return (
                                                                <DynamicAutocompleteInput
                                                                    key={field.stateName}
                                                                    label={field.placeholder}
                                                                    value={fieldValue}
                                                                    isEditable={isEditable}
                                                                    onSearch={getLocationPredictions}
                                                                    onChange={(val) => setExtraFieldValues((prev) => ({ ...prev, [fieldStoreKey]: val }))}
                                                                />
                                                            );
                                                        }

                                                        if (field.inputConfig.inputType === expenseInputTypes.PARTICIPANT) {
                                                            const sourceFromItem = field.apiKey
                                                                ? (item as unknown as Record<string, unknown>)[field.apiKey]
                                                                : null;
                                                            const participants = Array.isArray(storedValue)
                                                                ? storedValue.map(String)
                                                                : Array.isArray(sourceFromItem)
                                                                  ? sourceFromItem.map(String)
                                                                  : [];
                                                            const participantsLabel = participants.join(", ");
                                                            return (
                                                                <div key={field.stateName} className={isEditable ? "cursor-pointer" : ""}>
                                                                    <div
                                                                        onClick={() => {
                                                                            if (!isEditable) return;
                                                                            setParticipantsModalState({ key: fieldStoreKey, names: participants });
                                                                        }}
                                                                    >
                                                                        <Input
                                                                            label={field.placeholder}
                                                                            value={participantsLabel}
                                                                            locked={!isEditable}
                                                                            inputClassName="pr-10"
                                                                            placeholder={isEditable ? "Click to add participants" : undefined}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        if (field.inputConfig.inputType === expenseInputTypes.MONEY) {
                                                            const currencyStoreKey = field.inputConfig.currencyApiKey ?? "currencyCode";
                                                            const rawCurrency = extraFieldValues[currencyStoreKey];
                                                            const selectedCurrency =
                                                                typeof rawCurrency === "string" && rawCurrency.length > 0
                                                                    ? rawCurrency
                                                                    : (item.currencyCode ?? "USD");
                                                            const moneyValue = typeof storedValue === "string" ? storedValue : "";
                                                            const canEditMoney = showReviewActions && hasCategoryChanges && isEmpty;

                                                            return (
                                                                <div key={field.stateName} className="grid grid-cols-[2fr_1fr] gap-2">
                                                                    <Input
                                                                        label={field.placeholder}
                                                                        value={moneyValue}
                                                                        placeholder="0.00"
                                                                        locked={!canEditMoney}
                                                                        inputClassName="pr-10"
                                                                        onChange={
                                                                            canEditMoney
                                                                                ? (val) => {
                                                                                      if (/^\d*(\.\d{0,2})?$/.test(val)) {
                                                                                          setExtraFieldValues((prev) => ({ ...prev, [fieldStoreKey]: val }));
                                                                                      }
                                                                                  }
                                                                                : undefined
                                                                        }
                                                                    />
                                                                    <Input label="Currency" value={selectedCurrency} locked />
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <DynamicTextInput
                                                                key={field.stateName}
                                                                label={field.placeholder}
                                                                value={fieldValue}
                                                                isEditable={isEditable}
                                                                onCommit={(val) => setExtraFieldValues((prev) => ({ ...prev, [fieldStoreKey]: val }))}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}

                                    <ParticipantsModal
                                        isOpen={participantsModalState != null}
                                        initialNames={participantsModalState?.names ?? []}
                                        onClose={() => setParticipantsModalState(null)}
                                        onSave={(names) => {
                                            if (!participantsModalState) return;
                                            setExtraFieldValues((prev) => ({ ...prev, [participantsModalState.key]: names }));
                                            setParticipantsModalState(null);
                                        }}
                                    />

                                    {/* Charge date + Settle date */}
                                    {/* TODO: time not yet supported — date-only format */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <Input label="Charge Date" value={formatDate(item.chargeDate, "MMMM d, yyyy")} locked inputClassName="pr-10" />
                                        <Input label="Settle Date" value={formatDate(item.settleDate, "MMMM d, yyyy")} locked inputClassName="pr-10" />
                                    </div>

                                    {/* Policy violations — read-only banner above receipts (matches WebV5) */}
                                    <ExpenseDetailViolations
                                        violations={item.violations}
                                        onViewExplanation={() => setActiveDetailTab(DETAIL_TAB.Communications)}
                                    />

                                    {/* Receipts */}
                                    <div>
                                        <div className="mb-3 flex items-center justify-between gap-2">
                                            <h4 className="text-[30px] font-normal text-foreground">Receipt</h4>
                                            {!lostReceiptDeclaration?.declared && !isLoadingReceipt && receiptFiles.length > 1 && (
                                                <span className="text-sm text-gray-400">
                                                    Viewing {cardIndex + 1} of {receiptFiles.length}
                                                </span>
                                            )}
                                        </div>

                                        {lostReceiptDeclaration?.declared && (
                                            <div className="mb-3 space-y-3">
                                                <div className="flex items-center gap-2">
                                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-blue-600 text-white">
                                                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                                        </svg>
                                                    </span>
                                                    <span className="text-sm font-semibold text-foreground">Lost receipt</span>
                                                </div>

                                                <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                                                    <p className="text-xs font-bold uppercase tracking-wide text-amber-800">Receipt Acknowledgment</p>
                                                    <p className="mt-1 text-sm text-amber-800">
                                                        By entering your initials you confirm that this receipt has been lost and cannot be provided.
                                                    </p>
                                                    <div className="mt-3 inline-flex min-w-[72px] items-center justify-center rounded-md border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-foreground">
                                                        {lostReceiptDeclaration.initials}
                                                    </div>
                                                </div>

                                                {!!lostReceiptDeclaration.explanation && (
                                                    <div className="rounded-lg border border-green-300 bg-green-50 px-4 py-3">
                                                        <p className="text-sm font-semibold text-green-800">Explanation provided</p>
                                                        <button
                                                            type="button"
                                                            className="mt-1 text-sm font-medium text-green-700 underline"
                                                            onClick={() => setActiveDetailTab(DETAIL_TAB.Communications)}
                                                        >
                                                            View in Communications
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {!lostReceiptDeclaration?.declared &&
                                            (isLoadingReceipt ? (
                                                <div className="flex h-24 items-center justify-center rounded border border-gray-300 bg-gray-50">
                                                    <p className="text-sm text-gray-400">Loading receipts…</p>
                                                </div>
                                            ) : receiptFiles.length > 0 ? (
                                                // Intercept prev/next clicks from FilePreviewCard to sync our counter
                                                <div
                                                    onClick={(e) => {
                                                        const btn = (e.target as HTMLElement).closest("button[aria-label]");
                                                        if (!btn) return;
                                                        const label = btn.getAttribute("aria-label");
                                                        const total = receiptFiles.length;
                                                        if (label === "Previous file") setCardIndex((i) => (i - 1 + total) % total);
                                                        else if (label === "Next file") setCardIndex((i) => (i + 1) % total);
                                                    }}
                                                >
                                                    <FilePreviewCard
                                                        files={receiptFiles}
                                                        actionLabel="View"
                                                        previewHeight="h-96"
                                                        onAction={(_: unknown, index: number) => {
                                                            setFileViewerIndex(index);
                                                            setFileViewerOpen(true);
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="flex h-24 items-center justify-center rounded border border-dashed border-gray-300 text-sm text-gray-400">
                                                    No receipt available
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {activeDetailTab === DETAIL_TAB.History && (
                                <div>
                                    {loadingHistory ? (
                                        <div className="flex items-center gap-2 text-sm text-gray-400">
                                            <SpinnerIcon className="h-3.5 w-3.5 text-gray-400" />
                                            <span>Loading history…</span>
                                        </div>
                                    ) : historyError ? (
                                        <div className="flex h-40 flex-col items-center justify-center gap-3 text-sm text-gray-500">
                                            <p>{historyError}</p>
                                            <button
                                                type="button"
                                                onClick={loadHistory}
                                                className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 transition hover:bg-gray-50"
                                            >
                                                Retry
                                            </button>
                                        </div>
                                    ) : expenseHistory.length === 0 ? (
                                        <div className="flex h-40 items-center justify-center text-sm text-gray-400">No history available</div>
                                    ) : (
                                        <div className="space-y-8 py-4">
                                            {expenseHistory.map((h: any) => {
                                                const actor = (h.actor ?? {}) as { displayName?: string; userId?: string };
                                                const action = h.eventDisplay || `${h.event} by ${actor.displayName || "Unknown"}`;
                                                return <TimelineItem key={h.id} title={action} date={h.occurredAt} />;
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeDetailTab === DETAIL_TAB.Communications && (
                                <div>
                                    {loadingComments ? (
                                        <div className="flex items-center gap-2 text-sm text-gray-400">
                                            <SpinnerIcon className="h-3.5 w-3.5 text-gray-400" />
                                            <span>Loading comments…</span>
                                        </div>
                                    ) : commentsError ? (
                                        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{commentsError}</div>
                                    ) : violationComments.length === 0 && expenseComments.length === 0 && !lostReceiptDeclaration?.explanation ? (
                                        <div className="flex h-40 items-center justify-center text-sm text-gray-400">No communications available</div>
                                    ) : (
                                        <div className="space-y-3">
                                            {!!lostReceiptDeclaration?.explanation && (
                                                <CommentCard
                                                    variant={COMMENT_CARD_VARIANT.Violation}
                                                    title="Lost Receipt Explanation"
                                                    author={item?.submittedBy ?? ""}
                                                    date={lostReceiptDeclaration.declaredAt ?? ""}
                                                    policyName="Lost Receipt"
                                                    initials={lostReceiptDeclaration.initials}
                                                    text={lostReceiptDeclaration.explanation}
                                                />
                                            )}
                                            {[...violationComments]
                                                .sort((a, b) => compareDatesDesc(a?.createdAt, b?.createdAt))
                                                .map((c, idx) => (
                                                    <CommentCard
                                                        key={c.id ?? idx}
                                                        variant={COMMENT_CARD_VARIANT.Violation}
                                                        author={c.authorFullName}
                                                        date={c.createdAt}
                                                        policyName={c.policyName}
                                                        text={c.text}
                                                    />
                                                ))}
                                            {[...expenseComments]
                                                .sort((a, b) => compareDatesDesc(a?.createdAt, b?.createdAt))
                                                .map((c, idx) => (
                                                    <CommentCard
                                                        key={c.commentId ?? idx}
                                                        text={c.text}
                                                        author={c.authorFullName ?? c.authorId ?? ""}
                                                        date={c.createdAt}
                                                    />
                                                ))}
                                        </div>
                                    )}

                                    <div className="mt-6">
                                        <h4 className="mb-2 text-sm font-semibold text-gray-900">Add Comment or Question</h4>
                                        <textarea
                                            className="w-full rounded-lg border border-gray-300 p-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none"
                                            rows={4}
                                            value={newCommentText}
                                            onChange={(e) => setNewCommentText(e.target.value)}
                                            placeholder={composerDisabled ? "Comments disabled for approved reports" : "Type your message here..."}
                                            disabled={composerDisabled || !canWrite}
                                        />
                                        <div className="mt-3 flex items-center justify-between">
                                            <button
                                                type="button"
                                                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                                onClick={() => setNewCommentText("")}
                                                disabled={composerDisabled || !newCommentText}
                                            >
                                                Clear
                                            </button>
                                            <button
                                                type="button"
                                                className={`rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50 ${newCommentText.trim() ? "bg-blue-600 text-white hover:bg-blue-700" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                                                onClick={async () => {
                                                    if (!createExpenseComment || !item || !newCommentText.trim()) return;
                                                    try {
                                                        setSubmittingComment(true);
                                                        setCommentsError(null);
                                                        await createExpenseComment(item.id, newCommentText.trim());
                                                        setNewCommentText("");
                                                        // Refresh comments in the background without showing loading state
                                                        await loadCommunications();
                                                    } catch (err: unknown) {
                                                        const msg = err instanceof Error ? err.message : "Failed to post comment";
                                                        setCommentsError(msg);
                                                    } finally {
                                                        setSubmittingComment(false);
                                                    }
                                                }}
                                                disabled={composerDisabled || !newCommentText.trim() || submittingComment || !canWrite}
                                            >
                                                {submittingComment ? (
                                                    <span className="inline-flex items-center gap-2">
                                                        <SpinnerIcon className="h-3.5 w-3.5 text-current" />
                                                        Submitting
                                                    </span>
                                                ) : (
                                                    "Submit"
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Unsaved changes confirmation modal */}
                {pendingTab && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="unsaved-changes-title"
                        aria-describedby="unsaved-changes-desc"
                    >
                        <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
                            <h3 id="unsaved-changes-title" className="text-base font-semibold text-gray-900">
                                Unsaved Changes
                            </h3>
                            <p id="unsaved-changes-desc" className="mt-2 text-sm text-gray-600">
                                You have unsaved changes. Do you want to leave without saving?
                            </p>
                            <div className="mt-5 flex justify-end gap-3">
                                <button
                                    type="button"
                                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    onClick={() => setPendingTab(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    autoFocus
                                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                                    onClick={() => {
                                        handleCancelCategoryChanges();
                                        setActiveDetailTab(pendingTab);
                                        setPendingTab(null);
                                    }}
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="border-t border-gray-300 bg-white px-6 py-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                        {showReviewActions ? (
                            hasCategoryChanges ? (
                                <>
                                    <button
                                        type="button"
                                        disabled={isMutating || !item || footerActionLoading !== null}
                                        className="h-9 flex-1 rounded border border-blue-500 px-3 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                                        onClick={handleCancelCategoryChanges}
                                    >
                                        {footerActionLoading === "cancel" ? (
                                            <span className="inline-flex items-center gap-2">
                                                <SpinnerIcon className="h-3.5 w-3.5 text-current" />
                                                Cancel
                                            </span>
                                        ) : (
                                            "Cancel"
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={isMutating || !item || footerActionLoading !== null || !canWrite || isSelectedCategoryBlocked}
                                        className="h-9 flex-1 rounded bg-blue-600 px-3 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                                        onClick={() => {
                                            void handleSaveCategoryChanges();
                                        }}
                                    >
                                        {footerActionLoading === "save" ? (
                                            <span className="inline-flex items-center gap-2">
                                                <SpinnerIcon className="h-3.5 w-3.5 text-current" />
                                                Save Changes
                                            </span>
                                        ) : (
                                            "Save Changes"
                                        )}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        disabled={isMutating || !item || footerActionLoading !== null || isDenied || !canWrite}
                                        title={isDenied ? "The expense is already denied" : undefined}
                                        className="h-9 flex-1 rounded border border-red-400 bg-white px-3 text-xs font-medium text-red-500 hover:bg-red-50 disabled:opacity-50"
                                        onClick={handleFooterDeny}
                                    >
                                        {footerActionLoading === "deny" ? (
                                            <span className="inline-flex items-center gap-2">
                                                <SpinnerIcon className="h-3.5 w-3.5 text-current" />
                                                Deny
                                            </span>
                                        ) : (
                                            "Deny"
                                        )}
                                    </button>
                                    {isFooterApproveBlocked ? (
                                        <BlockedActionTooltip title={approveBlockedTooltip}>
                                            {(reveal) => (
                                                <TooltipTrigger
                                                    aria-disabled="true"
                                                    onPress={reveal}
                                                    className="h-9 w-full cursor-not-allowed rounded border border-green-600 bg-green-600 px-3 text-xs font-semibold text-white opacity-50 sm:w-auto sm:flex-1"
                                                >
                                                    Approve
                                                </TooltipTrigger>
                                            )}
                                        </BlockedActionTooltip>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled={isMutating || !item || footerActionLoading !== null || isApproved || !canWrite}
                                            title={isApproved ? "The expense is already approved" : undefined}
                                            className="h-9 flex-1 rounded border border-green-600 bg-green-600 px-3 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                                            onClick={() => {
                                                void handleFooterApprove();
                                            }}
                                        >
                                            {footerActionLoading === "approve" ? (
                                                <span className="inline-flex items-center gap-2">
                                                    <SpinnerIcon className="h-3.5 w-3.5 text-current" />
                                                    Approve
                                                </span>
                                            ) : (
                                                "Approve"
                                            )}
                                        </button>
                                    )}
                                </>
                            )
                        ) : (
                            <button
                                type="button"
                                className="h-9 flex-1 rounded border border-gray-300 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                onClick={onClose}
                            >
                                Close
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {updateErrorMessage && <UpdateExpenseErrorModal message={updateErrorMessage} onClose={() => setUpdateErrorMessage(null)} />}
            {violationPopup && <PolicyViolationModal {...violationPopup} onDismiss={() => setViolationPopup(null)} />}
        </>
    );
}

// ─── Date Range Button ────────────────────────────────────────────────────────

function DateRangeButton({ onApplyRange }: { onApplyRange: (range: ReportDateRange) => void }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState("30 Days");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const options = [
        { label: "7 Days", days: 7 },
        { label: "30 Days", days: 30 },
        { label: "90 Days", days: 90 },
        { label: "1 Year", days: 365 },
    ];

    useEffect(() => {
        if (!open) return;

        const handlePointerDown = (event: PointerEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener("pointerdown", handlePointerDown);
        return () => document.removeEventListener("pointerdown", handlePointerDown);
    }, [open]);

    const applyQuickSelect = (days: number, label: string) => {
        const today = new Date();
        const from = new Date(today);
        from.setDate(today.getDate() - (days - 1));

        const nextStartDate = toInputDateValue(from);
        const nextEndDate = toInputDateValue(today);

        setStartDate(nextStartDate);
        setEndDate(nextEndDate);
        setSelectedLabel(label);
        if (import.meta.env.DEV && (!isIsoDateKey(nextStartDate) || !isIsoDateKey(nextEndDate))) {
            console.warn("[smoke-check] DateRangeButton produced non-ISO filter values", {
                updatedDateFrom: nextStartDate,
                updatedDateTo: nextEndDate,
            });
        }
        onApplyRange({ updatedDateFrom: nextStartDate, updatedDateTo: nextEndDate });
        setOpen(false);
    };

    const applyCustomRange = () => {
        if (!startDate || !endDate) return;

        // Normalize in case the user picked a start date after the end date.
        const [rangeStart, rangeEnd] = startDate <= endDate ? [startDate, endDate] : [endDate, startDate];

        setSelectedLabel(`${fmtDate(rangeStart)} – ${fmtDate(rangeEnd)}`);
        if (import.meta.env.DEV && (!isIsoDateKey(rangeStart) || !isIsoDateKey(rangeEnd))) {
            console.warn("[smoke-check] DateRangeButton custom range is non-ISO", {
                updatedDateFrom: rangeStart,
                updatedDateTo: rangeEnd,
            });
        }
        onApplyRange({ updatedDateFrom: rangeStart, updatedDateTo: rangeEnd });
        setOpen(false);
    };

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                aria-expanded={open}
                aria-haspopup="dialog"
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-xs transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                onClick={() => setOpen((current) => !current)}
            >
                <span className="text-gray-400">
                    <IconCalendar />
                </span>
                {selectedLabel}
            </button>

            {open && (
                <div
                    role="dialog"
                    aria-label="Date range filter"
                    className="absolute right-0 z-[120] mt-2 w-[440px] max-w-[calc(100vw-2rem)] rounded-2xl border border-gray-200 bg-white p-5 shadow-xl ring-1 ring-black/5"
                >
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div className="flex flex-col">
                            <p className="mb-3 text-sm font-semibold text-gray-900">Custom Date Range</p>
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-medium text-gray-500" htmlFor="date-range-start">
                                        Start Date
                                    </label>
                                    <input
                                        id="date-range-start"
                                        type="date"
                                        value={startDate}
                                        onChange={(event) => setStartDate(event.target.value)}
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-xs transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-medium text-gray-500" htmlFor="date-range-end">
                                        End Date
                                    </label>
                                    <input
                                        id="date-range-end"
                                        type="date"
                                        value={endDate}
                                        onChange={(event) => setEndDate(event.target.value)}
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-xs transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                    />
                                </div>
                                <button
                                    type="button"
                                    disabled={!startDate || !endDate}
                                    className="mt-1 w-full rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white shadow-xs transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
                                    onClick={applyCustomRange}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <p className="mb-3 text-sm font-semibold text-gray-900">Quick Select</p>
                            <div className="flex flex-col gap-1 rounded-xl bg-gray-50 p-1.5">
                                {options.map((option) => {
                                    const isSelected = selectedLabel === option.label;
                                    return (
                                        <button
                                            key={option.label}
                                            type="button"
                                            aria-pressed={isSelected}
                                            className={`rounded-lg px-3 py-2.5 text-left text-sm transition ${
                                                isSelected ? "bg-white font-semibold text-blue-600 shadow-sm" : "font-medium text-gray-700 hover:bg-white/60"
                                            }`}
                                            onClick={() => applyQuickSelect(option.days, option.label)}
                                        >
                                            {option.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function IconDownload() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 3v12m0 0-4-4m4 4 4-4M3 17v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2" />
        </svg>
    );
}

// ─── Approval View ────────────────────────────────────────────────────────────

function ApprovalView() {
    // TODO: Enable the "Items" tab once its GraphQL endpoint is migrated to the
    //       same endpoint used by the "Reports" tab.
    const mainTabOptions = useMemo(
        () =>
            [
                { key: TAB.Reports, label: "Reports" },
                { key: TAB.Items, label: "Items", isDisabled: true },
            ] as const,
        [],
    );

    const [receiptViewerOpen, setReceiptViewerOpen] = useState(false);
    const [pendingApproveReportId, setPendingApproveReportId] = useState<string | null>(null);
    const [pendingApproveMessage, setPendingApproveMessage] = useState<string>("");
    const [pendingDenyReportId, setPendingDenyReportId] = useState<string | null>(null);
    const [pendingDenyMessage, setPendingDenyMessage] = useState<string>("");
    const [categoryBlockStatus, setCategoryBlockStatus] = useState<BlockedCategoryStatus | null>(null);
    const [cannotApproveState, setCannotApproveState] = useState<{ reportId: string; message: string; kinds: ApprovalBlockingKinds } | null>(null);

    const location = useLocation();
    const navigate = useNavigate();

    const {
        activeTab,
        setActiveTab,
        activeSubTab,
        setActiveSubTab,
        subTabOptions,
        previewReports,
        reviewReports,
        completedReports,
        reviewItems,
        completedItems,
        selectedItemId,
        setSelectedItemId,
        fullExpenseDetail,
        isLoadingReceipt,
        isLoading,
        isMutating,
        isProcessingReport,
        processingItemId,
        processingAction,
        error,
        approveItem,
        approveItems,
        denyItem,
        denyItems,
        pendingItem,
        updateExpenseCategory,
        approveReport,
        denyReport,
        getReportComments,
        getExpenseComments,
        getPolicyViolationComments,
        createExpenseComment,
        getExpenseHistory,
        setPage,
        pageInfo,
        reportSort,
        setReportSort,
        setReportColumnFilter,
        setReportDateRange,
        exportCompletedReports,
        isExporting,
        reportExpensesMap,
        loadReportExpenses,
        // Complete category list (active + deactivated + deleted); status-aware resolution picks the right subset.
        allCategories,
        categoriesStatus,
        reloadCategories,
        getLocationPredictions,
    } = useAccountingApproval();

    const canWrite = useCan(ACTIONS.manage.approvals.write);
    const { showError } = useNotifications();

    // Approvals are validated against the category list, so they stay blocked until it has loaded.
    const ensureCategoriesReady = useCallback((): boolean => {
        if (categoriesStatus === CATEGORIES_LOAD_STATUS.Ready) return true;
        showError(
            "Categories not loaded",
            categoriesStatus === CATEGORIES_LOAD_STATUS.Error
                ? "Unable to validate expense categories. Retry loading categories before approving."
                : "Expense categories are still loading. Please try again in a moment.",
        );
        return false;
    }, [categoriesStatus, showError]);

    // Approval is blocked (FE only) while any involved expense references a deactivated or deleted category.
    const findExpenseById = useCallback(
        (expenseId: string) => {
            if (fullExpenseDetail?.id === expenseId) return fullExpenseDetail;
            // Review items carry the legacy subCategoryId, so check them before the report cache.
            const listItem = reviewItems?.items.find((item) => item.id === expenseId);
            if (listItem) return listItem;
            for (const reportData of Object.values(reportExpensesMap)) {
                const reportItem = reportData.items.find((item) => item.id === expenseId);
                if (reportItem) return reportItem;
            }
            return undefined;
        },
        [fullExpenseDetail, reportExpensesMap, reviewItems],
    );

    const getExpenseCategoryBlockStatus = useCallback(
        (expenseId: string): BlockedCategoryStatus | undefined => {
            const expense = findExpenseById(expenseId);
            return expense ? getItemCategoryBlockStatus(expense, allCategories) : undefined;
        },
        [allCategories, findExpenseById],
    );

    const handleApproveItem = useCallback(
        (id: string) => {
            if (!ensureCategoriesReady()) return;
            const blockedStatus = getExpenseCategoryBlockStatus(id);
            if (blockedStatus) {
                setCategoryBlockStatus(blockedStatus);
                return;
            }
            const expense = findExpenseById(id);
            if (expense && hasBlockingViolation(expense)) {
                showError("Cannot approve expense", ITEM_VIOLATION_APPROVE_TOOLTIP);
                return;
            }
            void approveItem(id);
        },
        [approveItem, ensureCategoriesReady, findExpenseById, getExpenseCategoryBlockStatus, showError],
    );

    const handleApproveItems = useCallback(
        (ids: string[]) => {
            if (!ensureCategoriesReady()) return;
            const blockedStatus = ids.map(getExpenseCategoryBlockStatus).find((status) => status !== undefined);
            if (blockedStatus) {
                setCategoryBlockStatus(blockedStatus);
                return;
            }
            // Only the selected expenses are evaluated; the category block above always leads.
            const selected = ids.flatMap((id) => findExpenseById(id) ?? []);
            const kinds = getApprovalBlockingKinds(selected);
            if (kinds.explanation || kinds.receipt) {
                const blockedExpense = selected.find(hasBlockingViolation);
                const reportId = (blockedExpense?.reportId ?? "").replace(/^Report#/, "");
                setCannotApproveState({ reportId, message: "", kinds });
                return;
            }
            void approveItems(ids);
        },
        [approveItems, ensureCategoriesReady, findExpenseById, getExpenseCategoryBlockStatus],
    );

    const getReportCategoryBlockStatus = useCallback(
        (reportId: string): BlockedCategoryStatus | undefined =>
            (reportExpensesMap[reportId]?.items ?? []).map((item) => getItemCategoryBlockStatus(item, allCategories)).find((status) => status !== undefined),
        [allCategories, reportExpensesMap],
    );

    const routeSubTab = getApprovalSubTabFromSearch(location.search);
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const requestedTab = params.get(QUERY_PARAM.tab);

    useEffect(() => {
        if (activeSubTab !== routeSubTab) {
            setActiveSubTab(routeSubTab);
        }
    }, [activeSubTab, routeSubTab, setActiveSubTab]);

    useEffect(() => {
        const canonicalSearch = `?${QUERY_PARAM.tab}=${routeSubTab}`;
        if (location.pathname === ROUTE_PATH.approval && (location.search !== canonicalSearch || location.hash)) {
            const nextParams = new URLSearchParams({ [QUERY_PARAM.tab]: routeSubTab });
            navigate(`${ROUTE_PATH.approval}?${nextParams.toString()}`, { replace: true });
        }
    }, [location.hash, location.pathname, location.search, navigate, routeSubTab]);

    useEffect(() => {
        if (requestedTab && !isValidApprovalTab(requestedTab)) {
            showError("Tab not found", "The requested approval tab does not exist.");
        }
    }, [requestedTab, showError]);

    const currentReportData =
        activeTab === TAB.Reports
            ? activeSubTab === SUB_TAB.Preview
                ? previewReports
                : activeSubTab === SUB_TAB.Review
                  ? reviewReports
                  : activeSubTab === SUB_TAB.Completed
                    ? completedReports
                    : null
            : null;

    const currentItemData =
        activeTab === TAB.Items ? (activeSubTab === SUB_TAB.Review ? reviewItems : activeSubTab === SUB_TAB.Completed ? completedItems : null) : null;

    // The date range filter and Export button are exclusive to the Completed reports tab (APP-406).
    const showCompletedControls = activeTab === TAB.Reports && activeSubTab === SUB_TAB.Completed;

    const displayReportData = currentReportData;

    const receiptFiles = useMemo<FileViewerFile[]>(
        () =>
            (fullExpenseDetail?.receipts ?? [])
                .filter((r) => r.s3URL)
                .map((r) => ({
                    id: r.receiptId,
                    url: r.s3URL!,
                    mimeType: (r.fileType ?? MimeType.JPEG) as MimeType,
                    name: r.receiptId,
                })),
        [fullExpenseDetail],
    );

    const handleViewReceipt = useCallback(
        (itemId: string) => {
            setSelectedItemId(itemId);
            setReceiptViewerOpen(true);
        },
        [setSelectedItemId],
    );

    return (
        <ApprovalWriteAccessContext.Provider value={canWrite}>
            {/* Page heading */}
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Approval</h1>
                    <p className="mt-1 text-sm font-semibold text-gray-500">
                        Perform approval reviews in the &ldquo;Review&rdquo; section. Preview shows the latest reports across all statuses, and Completed shows
                        previously approved items.
                    </p>
                </div>

                {showCompletedControls && (
                    <Button
                        color="secondary"
                        size="md"
                        iconLeading={IconDownload}
                        isDisabled={isExporting || !completedReports || completedReports.reports.length === 0}
                        isLoading={isExporting}
                        onClick={() => {
                            void exportCompletedReports();
                        }}
                    >
                        Export
                    </Button>
                )}
            </div>

            {/* Error */}
            {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
            {categoriesStatus === CATEGORIES_LOAD_STATUS.Error && (
                <div
                    role="alert"
                    className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                    <span>Unable to load expense categories. Approvals are blocked until they load.</span>
                    <Button color="secondary" size="sm" onClick={() => void reloadCategories()}>
                        Retry
                    </Button>
                </div>
            )}

            {/* Reports / Items tab toggle */}
            <div className="mb-4">
                <TabToggle activeTab={activeTab} options={mainTabOptions} onTabChange={setActiveTab} />
            </div>

            {/* Preview / Review / Completed sub-tabs + date filter */}
            <div className="mb-5 flex flex-col gap-3 border-b border-gray-200 pb-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap gap-5">
                    {subTabOptions.map((tab) => {
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                className={`pb-2.5 text-sm capitalize transition ${
                                    activeSubTab === tab.key
                                        ? "border-b-2 border-blue-600 font-semibold text-blue-600"
                                        : tab.isDisabled
                                          ? "cursor-not-allowed text-gray-300"
                                          : "text-gray-500 hover:text-gray-800"
                                }`}
                                onClick={() => {
                                    // The URL is the single source of truth for the active sub-tab: the
                                    // routeSubTab effect syncs state from `location.search`. Setting the
                                    // sub-tab state here as well caused a flash — the optimistic update
                                    // was momentarily reverted by that effect before the navigation
                                    // propagated the new query param.
                                    navigate(SUB_TAB_TO_ROUTE[tab.key]);
                                }}
                                disabled={tab.isDisabled}
                                aria-disabled={tab.isDisabled}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {showCompletedControls && (
                    <DateRangeButton
                        onApplyRange={(range) => {
                            setReportDateRange(range);
                        }}
                    />
                )}
            </div>

            {/* Content */}
            {activeTab === TAB.Reports && (
                <ReportsTable
                    key={`reports-${activeSubTab}`}
                    data={displayReportData}
                    subTab={activeSubTab}
                    isLoading={isLoading}
                    isMutating={isMutating}
                    isProcessingReport={isProcessingReport}
                    processingItemId={processingItemId}
                    processingAction={processingAction}
                    onApproveItem={handleApproveItem}
                    onDenyItem={(id) => void denyItem(id)}
                    onPendingItem={(id) => void pendingItem(id)}
                    onApproveReport={(id, message) => {
                        if (!ensureCategoriesReady()) return;
                        const blockedStatus = getReportCategoryBlockStatus(id);
                        if (blockedStatus) {
                            setCategoryBlockStatus(blockedStatus);
                            return;
                        }
                        const reportData = reportExpensesMap[id];
                        // Evaluates the whole report; a category block above always shows alone.
                        const blockingKinds = getApprovalBlockingKinds(reportData?.items ?? []);
                        if (blockingKinds.explanation || blockingKinds.receipt) {
                            setCannotApproveState({ reportId: id, message: message ?? "", kinds: blockingKinds });
                            return;
                        }
                        const hasPending = reportData ? reportData.items.some((item) => item.status === ItemStatusValue.Pending) : false;
                        if (hasPending) {
                            setPendingApproveReportId(id);
                            setPendingApproveMessage(message ?? "");
                        } else {
                            void approveReport(id, message?.trim() || undefined);
                        }
                    }}
                    onDenyReport={(id, message) => {
                        setPendingDenyReportId(id);
                        setPendingDenyMessage(message ?? "");
                    }}
                    getReportComments={getReportComments}
                    onSelectItem={setSelectedItemId}
                    pageInfo={pageInfo}
                    onPageChange={setPage}
                    sortKey={reportSort?.sortBy ?? null}
                    sortDirection={reportSort?.sortOrder ?? "asc"}
                    onSort={(sortBy) => {
                        let next: { sortBy: ReportSortBy; sortOrder: "asc" | "desc" } | null;
                        if (reportSort?.sortBy !== sortBy) {
                            next = { sortBy, sortOrder: "asc" };
                        } else if (reportSort.sortOrder === "asc") {
                            next = { sortBy, sortOrder: "desc" };
                        } else {
                            next = null; // 3rd click clears sort
                        }
                        setReportSort(next);
                    }}
                    onFilter={setReportColumnFilter}
                    reportExpensesMap={reportExpensesMap}
                    loadReportExpenses={loadReportExpenses}
                    onViewReceipt={handleViewReceipt}
                    allCategories={allCategories}
                />
            )}

            {activeSubTab === SUB_TAB.Preview && activeTab === TAB.Items && (
                <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-500">
                    Preview is available in the Reports view and shows the latest reports.
                </div>
            )}

            {activeSubTab !== SUB_TAB.Preview && activeTab === TAB.Items && (
                <ItemsTable
                    key={`items-${activeSubTab}`}
                    data={currentItemData}
                    subTab={activeSubTab}
                    isLoading={isLoading}
                    isMutating={isMutating}
                    processingItemId={processingItemId}
                    processingAction={processingAction}
                    onApproveItem={handleApproveItem}
                    onApproveItems={handleApproveItems}
                    onDenyItem={(id) => void denyItem(id)}
                    onDenyItems={(ids) => void denyItems(ids)}
                    onPendingItem={(id) => void pendingItem(id)}
                    onSelectItem={setSelectedItemId}
                    onViewReceipt={handleViewReceipt}
                    allCategories={allCategories}
                />
            )}

            {/* Confirm approve report modal */}
            {pendingApproveReportId && (
                <ConfirmApproveModal
                    isProcessing={isProcessingReport}
                    onConfirm={() => {
                        const msg = pendingApproveMessage.trim() || undefined;
                        void (msg ? approveReport(pendingApproveReportId, msg) : approveReport(pendingApproveReportId)).finally(() => {
                            setPendingApproveReportId(null);
                            setPendingApproveMessage("");
                        });
                    }}
                    onCancel={() => {
                        setPendingApproveReportId(null);
                        setPendingApproveMessage("");
                    }}
                />
            )}

            {cannotApproveState && (
                <CannotApproveReportModal
                    kinds={cannotApproveState.kinds}
                    isProcessing={isProcessingReport}
                    onCancel={() => setCannotApproveState(null)}
                    onSendBack={() => {
                        const { reportId, message } = cannotApproveState;
                        const trimmedMessage = message.trim() || undefined;
                        void (trimmedMessage ? denyReport(reportId, trimmedMessage) : denyReport(reportId)).finally(() => setCannotApproveState(null));
                    }}
                />
            )}

            {pendingDenyReportId && (
                <ConfirmDenyModal
                    isProcessing={isProcessingReport}
                    onConfirm={() => {
                        const msg = pendingDenyMessage.trim() || undefined;
                        void (msg ? denyReport(pendingDenyReportId, msg) : denyReport(pendingDenyReportId)).finally(() => {
                            setPendingDenyReportId(null);
                            setPendingDenyMessage("");
                        });
                    }}
                    onCancel={() => {
                        setPendingDenyReportId(null);
                        setPendingDenyMessage("");
                    }}
                />
            )}

            {/* Expense detail slide-over */}
            {selectedItemId && !receiptViewerOpen && (
                <ExpenseDetailPanel
                    item={fullExpenseDetail}
                    allCategories={allCategories}
                    isLoadingReceipt={isLoadingReceipt}
                    isMutating={isMutating}
                    processingItemId={processingItemId}
                    processingAction={processingAction}
                    showReviewActions={activeSubTab === SUB_TAB.Review}
                    onApprove={handleApproveItem}
                    onDeny={(itemId) => {
                        void denyItem(itemId);
                    }}
                    onPending={(itemId) => {
                        void pendingItem(itemId);
                    }}
                    getLocationPredictions={getLocationPredictions}
                    onUpdateExpenseCategory={(itemId, selectedCategory, fieldOverrides) => updateExpenseCategory(itemId, selectedCategory, fieldOverrides)}
                    getExpenseComments={getExpenseComments}
                    getPolicyViolationComments={getPolicyViolationComments}
                    createExpenseComment={createExpenseComment}
                    getExpenseHistory={getExpenseHistory}
                    onClose={() => setSelectedItemId(null)}
                />
            )}

            {categoryBlockStatus && (
                <UpdateExpenseErrorModal
                    title={BLOCKED_CATEGORY_COPY[categoryBlockStatus].title}
                    message={BLOCKED_CATEGORY_COPY[categoryBlockStatus].message}
                    onClose={() => setCategoryBlockStatus(null)}
                />
            )}

            {/* Receipt FileViewer */}
            <FileViewer
                files={receiptFiles}
                isOpen={receiptViewerOpen && !isLoadingReceipt}
                onOpenChange={(open: boolean) => {
                    setReceiptViewerOpen(open);
                    if (!open) setSelectedItemId(null);
                }}
                initialIndex={0}
            />
        </ApprovalWriteAccessContext.Provider>
    );
}

// ─── Approval Page ────────────────────────────────────────────────────────────

export function AccountingApprovalPage() {
    return <ApprovalView />;
}
